import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  approveCollectionBuild,
  generateCollectionBuildLooks,
  generateCollectionBuildMoodboard,
  getCollectionBuildErrorMessage,
  regenerateCollectionBuild,
  setCollectionBuildLike,
  startCollectionBuildDesignSheet,
  submitCollectionBuildFeedback,
  CollectionBuildHistoryRecord,
  CollectionBuildSessionPayload,
} from "../../app/api/collectionBuild";
import {
  ImageGeneratorFeedbackReason,
  ImageGeneratorLookView,
} from "../../app/api/imageGenerator";
import CollapseIcon from "../../assets/icons/CollapseIcon";
import CollectionBuildDesignDetails from "./CollectionBuildDesignDetails";
import { useCollectionBuildSessions } from "./CollectionBuildSessionsContext";
import ImageGeneratorBusyOverlay from "./ImageGeneratorBusyOverlay";
import {
  CopyIcon,
  DownloadIcon,
  ExpandIcon,
  RegenerateIcon,
  ThumbIcon,
  expandGlyphCompactClassName,
  generativeDownloadMenuClassName,
  generativeDownloadMenuItemClassName,
  generativeResultToolBtnClassName,
  generativeResultToolBtnCompactClassName,
  iconGlyphClassName,
  iconGlyphCompactClassName,
} from "./shared/generativeResultToolbarIcons";
import useGenerativeResultPreviews from "./shared/useGenerativeResultPreviews";
import {
  CollectionBuildLook,
  CollectionBuildLookVersion,
  CollectionBuildMoodboardVersion,
  CollectionBuildResultDetails,
  CollectionBuildSheetVersion,
  SilhouetteGroup,
  applyLooksToSilhouetteGroups,
  areSilhouetteGroupsComplete,
  collectionBuildAfterGenerationCopy,
  collectionBuildBorderClassName,
  collectionBuildDividerClassName,
  collectionBuildRegenPromptCopy,
  createLooksFromGroups,
  createSilhouetteGroups,
  exclusivePillClassName,
  helperTextClassName,
  getLookBookPageCount,
  getLookBookPageForIndex,
  LOOK_BOOK_PAGE_SIZE,
  lookBookPageSlotCount,
  lookCountFromRangeBreakdown,
  lookImagePairAt,
  lookVersionPairReady,
  outlineButtonClassName,
  scrollCollectionSectionIntoView,
  sliceLookBookPage,
  snapshotCollectionBuildDetails,
  submitButtonClassName,
  textareaClassName,
  withSequentialLookNumbers,
} from "./collectionBuildConstants";
import {
  collectionLooksApproved,
  collectionMoodboardApproved,
  collectionBuildFailedOutput,
  collectionBuildGenerationErrorCopy,
  collectionBuildOutputsPending,
  collectionBuildSessionSearchParams,
  collectionLooksFromOutputs,
  collectionMoodboardsFromOutputs,
  mergeCollectionLookApprovals,
  moodboardGenerationIdForLooks,
  collectionSheetsFromOutputs,
  ensureCollectionBuildSessionId,
  looksPayloadFromGroups,
  looksPayloadFromSessionOutputs,
  mergeCollectionBuildDetailsPreferences,
  parseCollectionBuildSessionId,
  preferencesFromCollectionSession,
  resolveCollectionBuildApprovedSheetOutputId,
  sanitizeCollectionBuildSessionPayload,
  toCollectionBuildOutputId,
  type CollectionBuildGenerationErrorCopy,
  type CollectionBuildGenerationKind,
} from "./collectionBuildSession";
import {
  downloadImageGeneratorLook,
  downloadImageGeneratorLookPdf,
  downloadImageGeneratorLooks,
  ImageGeneratorDownloadFormat,
} from "./imageGeneratorLookExport";

type Props = {
  details: CollectionBuildResultDetails;
  sessionId?: number;
  initialSilhouetteGroups?: SilhouetteGroup[];
  enqueueError?: string | null;
  onBackToQuery: () => void;
};

const toFeedbackReason = (reason: string): ImageGeneratorFeedbackReason => {
  if (reason.startsWith("Incorrect")) {
    return "incorrect";
  }
  if (reason === "Blur") {
    return "blur";
  }
  if (reason.startsWith("Style")) {
    return "style";
  }
  return "other";
};

const looksFromRecords = (
  records: CollectionBuildHistoryRecord[],
  fallbackGroups?: SilhouetteGroup[]
) => {
  const fromSession = collectionLooksFromOutputs(records);
  if (fromSession.length) {
    return withSequentialLookNumbers(fromSession);
  }
  return fallbackGroups?.length
    ? withSequentialLookNumbers(createLooksFromGroups(fallbackGroups))
    : [];
};

/** Explicit return type avoids circular `any` when callers assign into moodboard state. */
const moodboardsWithImages = (
  outputs: CollectionBuildHistoryRecord[]
): CollectionBuildMoodboardVersion[] =>
  collectionMoodboardsFromOutputs(outputs).filter(item =>
    Boolean(item.imageUrl)
  );

/** Looks that were started for a design-sheet run (same id as collectionLooksFromOutputs). */
const designSheetLookIdsFromOutputs = (
  outputs: CollectionBuildHistoryRecord[]
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  outputs.forEach(item => {
    if (item.outputType !== "look" || !item.details?.designSheet?.status) {
      return;
    }
    const id = `look-${item.outputNumber || item.id}`;
    if (seen.has(id)) {
      return;
    }
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

const seedApprovedLookOutputIds = (
  outputs: CollectionBuildHistoryRecord[],
  into: Set<number>
) => {
  outputs.forEach(item => {
    if (item.outputType !== "look" || !item.review?.approvedAt) {
      return;
    }
    const id = toCollectionBuildOutputId(item.id);
    if (id) {
      into.add(id);
    }
  });
};

type GenerationFailure = CollectionBuildGenerationErrorCopy & {
  outputType: CollectionBuildGenerationKind;
  outputId: number | null;
};

/** Furthest results stage to bring into view after open / first appear. */
type LatestGenerationStage = "moodboard" | "looks" | "design-sheet";

const LATEST_GENERATION_STAGE_RANK: Record<LatestGenerationStage, number> = {
  moodboard: 1,
  looks: 2,
  "design-sheet": 3,
};

/**
 * Generation stage section shell. Active stages add min-h for a full viewport;
 * prior stages stay content-sized so scroll height does not jump / fight snap.
 * No snap-start — proximity snap + stage remounts caused bounce between moodboard/looks.
 */
const STAGE_SECTION_CLASS = "flex w-full min-w-0 flex-col scroll-mt-2 py-3";

const STAGE_SECTION_ACTIVE_MIN_H_CLASS = "min-h-[calc(100vh-11rem)]";

/**
 * Side columns match the results column height on xl+ (results define row height).
 * `h-0 min-h-full` stops Design Details content from stretching the grid taller.
 */
const STAGE_SIDE_MATCH_HEIGHT_CLASS =
  "flex min-h-0 min-w-0 flex-col overflow-hidden xl:h-0 xl:min-h-full";

const sessionHasReadyGeneration = (
  outputs: CollectionBuildHistoryRecord[] | null | undefined
) => {
  if (!outputs?.length) {
    return false;
  }
  if (Object.keys(collectionSheetsFromOutputs(outputs)).length) {
    return true;
  }
  if (looksFromRecords(outputs).some(look => look.versions[0]?.outputId)) {
    return true;
  }
  return collectionMoodboardsFromOutputs(outputs).some(item =>
    Boolean(item.imageUrl)
  );
};

const CollectionBuildGenerationError: React.FC<{
  title: string;
  reason: string;
  retrying?: boolean;
  onRetry?: () => void;
  onBackToQuery: () => void;
}> = ({ title, reason, retrying, onRetry, onBackToQuery }) => (
  <div
    className={`flex min-h-[min(28rem,calc(100vh-18rem))] flex-col items-center justify-center rounded-2xl border ${collectionBuildBorderClassName} bg-[#D3C0B626] px-6 py-16 text-center`}
    role="alert"
  >
    <p className="font-avenir text-lg font-semibold text-secondary">{title}</p>
    <p className="mt-2 max-w-md font-avenir text-sm text-tertiary">{reason}</p>
    <div className="mt-8 flex flex-wrap justify-center gap-3">
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className={submitButtonClassName}
        >
          {retrying ? "Retrying…" : "Retry"}
        </button>
      ) : null}
      <button
        type="button"
        onClick={onBackToQuery}
        disabled={retrying}
        className={outlineButtonClassName}
      >
        Back to query
      </button>
    </div>
  </div>
);

class CollectionBuildResultsBoundary extends React.Component<
  {
    children: React.ReactNode;
    resetKey?: string | number;
    onBackToQuery: () => void;
  },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: CollectionBuildResultsBoundary["props"]) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <CollectionBuildGenerationError
          title="Couldn't show this collection"
          reason="Something went wrong while opening the results. Retry generation or go back to edit your selections."
          onBackToQuery={this.props.onBackToQuery}
        />
      );
    }
    return this.props.children;
  }
}

const POSITIVE_FEEDBACK_REASONS = [
  "Great composition",
  "Colors work well",
  "On-brief",
  "Other",
] as const;

const NEGATIVE_FEEDBACK_REASONS = [
  "Incorrect or incomplete",
  "Blur",
  "Style or look",
  "Other",
] as const;

const lookOnImageBtnClassName = generativeResultToolBtnCompactClassName;

const lookNumberBadgeClassName =
  "pointer-events-none absolute left-2 top-2 z-20 flex h-[22px] min-w-[22px] items-center justify-center rounded-md bg-white px-1.5 text-[12px] font-semibold leading-none text-[#2F2F2F] shadow-[0_1px_2px_rgba(0,0,0,0.1)]";

const mutedButtonClassName =
  "h-[46px] rounded-full bg-gray px-8 font-avenir text-sm text-secondary transition-colors hover:bg-lightGray focus:outline-none";

const outlineActionClassName = `${outlineButtonClassName} disabled:cursor-not-allowed disabled:opacity-40`;

/** Matches ImageGeneratorInlineResult history/rail collapse control. */
const renderRailButtonClassName =
  "inline-flex h-8 shrink-0 items-center gap-1 rounded-[100px] border border-[#737373] bg-white px-3 py-1 font-avenir text-xs font-medium leading-none text-secondary transition-colors hover:bg-lightGray focus:outline-none";

const promptInputBaseClassName = `w-full resize-none rounded-2xl border ${collectionBuildBorderClassName} bg-white px-4 py-3 font-avenir text-sm text-secondary placeholder:text-secondary focus:border-secondary focus:outline-none`;
const promptInputClassName = `min-h-[99px] ${promptInputBaseClassName}`;
const promptInputCompactClassName = `min-h-[72px] ${promptInputBaseClassName}`;

const saveInputClassName = `h-10 w-full rounded-lg border ${collectionBuildBorderClassName} bg-white px-3 font-avenir text-sm text-secondary placeholder:text-tertiary focus:border-secondary focus:outline-none`;

/** Tan fill matches look-selection chips / Apply-to-multiple in Figma. */
const regenActiveFillClassName = "bg-[#E4D9CF] text-secondary";

const regenChipClassName = (active: boolean) =>
  `inline-flex h-8 items-center gap-1.5 rounded-full border ${collectionBuildBorderClassName} px-3.5 font-avenir text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
    active
      ? regenActiveFillClassName
      : "bg-white text-secondary hover:bg-lightGray"
  }`;

const facePillClassName = (active: boolean) =>
  `h-8 rounded-full border ${collectionBuildBorderClassName} px-5 font-avenir text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
    active
      ? regenActiveFillClassName
      : "bg-white text-secondary hover:bg-lightGray"
  }`;

const lookTabClassName = (active: boolean) =>
  `h-[30px] rounded-full border ${collectionBuildBorderClassName} px-3 text-[14px] font-medium font-avenir focus:outline-none ${
    active
      ? "bg-[#DFD6CD] text-primary"
      : "bg-white text-secondary hover:bg-lightGray"
  }`;

const MOCK_GENERATE_DELAY_MS = 4200;
/** Full result canvas while Collection Build generates — one cream card, no nested loader card.
 *  min-h-[40rem] (640px) keeps the busy stage taller than Design Details (~552px max). */
const inlineBusyCanvasClassName =
  "relative h-full w-full min-h-[40rem] self-stretch overflow-hidden rounded-lg border-2 border-[#E8C4B8] bg-[#F7F3EE]";
type CollectionBuildBusyKind = "moodboard" | "look" | "design-sheet";

/** Primary loading title for the active Collection Build generation kind. */
const collectionBuildBusyTitle = (
  kind: CollectionBuildBusyKind,
  mode: "generate" | "regen" = "generate"
) => {
  if (mode === "regen") {
    if (kind === "moodboard") {
      return "Refreshing moodboard…";
    }
    if (kind === "look") {
      return "Refreshing looks…";
    }
    return "Refreshing design sheet…";
  }
  if (kind === "moodboard") {
    return "Generating moodboard…";
  }
  if (kind === "look") {
    return "Generating looks…";
  }
  return "Generating design sheet…";
};

const InlineBusyCanvas: React.FC<{
  mode: "generate" | "regen";
  kind: CollectionBuildBusyKind;
  className?: string;
}> = ({ mode, kind, className = "" }) => {
  const title = collectionBuildBusyTitle(kind, mode);
  // Outer peach-bordered cream frame IS the loading surface. Compact doodle
  // only (no nested bordered box); title/subtitle live on the same canvas.
  return (
    <div
      className={`${inlineBusyCanvasClassName} flex flex-col items-center justify-center px-6 py-16 text-center ${className}`}
      role="status"
      aria-busy="true"
      aria-label={title}
    >
      {/* Compact doodle is 72×58; scale to full overlay size without a nested card. */}
      <div className="flex h-[168px] w-[134px] shrink-0 items-center justify-center">
        <div className="relative h-[72px] w-[58px] scale-[2.33]">
          <ImageGeneratorBusyOverlay
            mode={mode}
            outputType="Style"
            variant="inline"
            compact
          />
        </div>
      </div>
      <p className="mt-6 text-sm font-semibold text-gray-900">{title}</p>
      <p className="mt-1 text-xs text-gray-600">
        Stay on this screen while we work.
      </p>
    </div>
  );
};

/** Front+back generating animation used until both look URLs exist. */
const LookPairBusyFaces: React.FC<{
  mode?: "generate" | "regen";
}> = ({ mode = "generate" }) => (
  <div className="flex w-full">
    <div
      className={`relative aspect-[3/4] min-w-0 flex-1 overflow-hidden bg-[#F7F3EE] border-r ${collectionBuildDividerClassName}`}
    >
      <ImageGeneratorBusyOverlay
        mode={mode}
        outputType="Style"
        variant="inline"
        compact
      />
    </div>
    <div className="relative aspect-[3/4] min-w-0 flex-1 overflow-hidden bg-[#F7F3EE]">
      <ImageGeneratorBusyOverlay
        mode={mode}
        outputType="Style"
        variant="inline"
        compact
      />
    </div>
  </div>
);

const lookVersionShowGenerating = (
  version?: CollectionBuildLookVersion | null
): boolean =>
  !lookVersionPairReady(version) &&
  version?.status !== "failed" &&
  version?.status !== "generated";

/** Look-book sized busy placeholders — one card per expected look (max page size). */
const LooksBusyGrid: React.FC<{
  mode: "generate" | "regen";
  slotCount: number;
}> = ({ mode, slotCount }) => {
  const title = collectionBuildBusyTitle("look", mode);
  const slots = lookBookPageSlotCount(slotCount);
  return (
    <div
      className="flex w-full min-w-[14rem] max-w-full flex-col gap-3"
      role="status"
      aria-busy="true"
      aria-label={title}
    >
      <div
        className={`grid w-full grid-cols-1 gap-5 ${
          slots > 1 ? "md:grid-cols-2" : ""
        }`}
      >
        {Array.from({ length: slots }, (_, index) => (
          <article
            key={`look-busy-${index}`}
            className={`relative w-full min-w-[14rem] overflow-hidden rounded-lg border-2 border-[#E8C4B8] bg-[#F7F3EE] ${
              slots === 1 ? "max-w-[min(100%,36rem)]" : ""
            }`}
          >
            <LookPairBusyFaces mode={mode} />
          </article>
        ))}
      </div>
      <p className="text-center text-sm font-semibold text-gray-900">{title}</p>
      <p className="text-center text-xs text-gray-600">
        Feel free to come back in a few seconds while we ready this for you!
      </p>
    </div>
  );
};

type LookFace = "front" | "back";
type LooksPhase = "hidden" | "generating" | "ready";
type SheetsPhase = "hidden" | "generating" | "ready";
type MultiSelectPurpose = "regen" | "design-sheet";
type RegenApplyMode = "all" | "multiple";

type LightboxTarget = {
  lookId: string;
  face: LookFace;
  url: string;
};

const lookFaceKey = (lookId: string, face: LookFace) => `${lookId}:${face}`;

const formatSelectedLooks = (items: CollectionBuildLook[], ids: string[]) => {
  const names = items
    .filter(item => ids.includes(item.id))
    .map(item => `Look ${item.number}`);
  if (!names.length) {
    return "";
  }
  if (names.length === 1) {
    return `*Selected ${names[0]}`;
  }
  if (names.length === 2) {
    return `*Selected ${names[0]} and ${names[1]}`;
  }
  return `*Selected ${names.slice(0, -1).join(", ")} and ${
    names[names.length - 1]
  }`;
};

/** Same check glyph as ImageGeneratorDesignSheetDownloadModal. */
const CheckboxCheckIcon: React.FC = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden>
    <path
      d="M1 4.2 3.6 6.8 9 1.2"
      stroke="#FFFFFF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LookSelectCheckbox: React.FC<{
  checked: boolean;
  label: string;
  onToggle: () => void;
}> = ({ checked, label, onToggle }) => (
  <label className="flex cursor-pointer items-center gap-2 text-sm text-secondary">
    <input
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      className="sr-only"
    />
    <span
      aria-hidden
      className={`flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[2px] border ${
        checked
          ? "border-[#5A5A5A] bg-[#5A5A5A] text-white"
          : `${collectionBuildBorderClassName} bg-white`
      }`}
    >
      {checked ? <CheckboxCheckIcon /> : null}
    </span>
    {label}
  </label>
);

const sheetToolBtnClassName = generativeResultToolBtnClassName;

const moodboardToolBtnClassName = generativeResultToolBtnClassName;

/** Stage header download — match Render pill height for shared horizontal plane. */
const stageHeaderDownloadBtnClassName =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-[#4D4D4D] bg-[#F5F5F5] text-[#212121] transition-colors hover:bg-[#EBEBEB] focus:outline-none disabled:cursor-not-allowed disabled:opacity-40";

const MOODBOARD_DOWNLOAD_FORMATS: Array<{
  value: ImageGeneratorDownloadFormat;
  label: string;
}> = [
  { value: "pdf", label: "PDF" },
  { value: "jpeg", label: "JPEG" },
  { value: "png", label: "PNG" },
  { value: "svg", label: "SVG" },
];

const LOOK_DOWNLOAD_FORMATS = MOODBOARD_DOWNLOAD_FORMATS;

const DESIGN_SHEET_DOWNLOAD_FORMATS = MOODBOARD_DOWNLOAD_FORMATS;

const LookImageDownloadMenu: React.FC<{
  disabled?: boolean;
  downloading?: boolean;
  open: boolean;
  onToggle: () => void;
  onPick: (format: ImageGeneratorDownloadFormat) => void;
}> = ({ disabled, downloading, open, onToggle, onPick }) => (
  <div className="relative" data-look-download>
    <button
      type="button"
      disabled={disabled || downloading}
      onClick={event => {
        event.stopPropagation();
        onToggle();
      }}
      className={lookOnImageBtnClassName}
      aria-label="Download"
      aria-haspopup="menu"
      aria-expanded={open}
    >
      <DownloadIcon className={iconGlyphCompactClassName} />
    </button>
    {open ? (
      <div
        role="menu"
        data-look-download
        className={`${generativeDownloadMenuClassName} bottom-full right-0 mb-2`}
        onClick={event => event.stopPropagation()}
      >
        {LOOK_DOWNLOAD_FORMATS.map(option => (
          <button
            key={option.value}
            type="button"
            role="menuitem"
            onClick={() => onPick(option.value)}
            className={generativeDownloadMenuItemClassName}
          >
            {option.label}
          </button>
        ))}
      </div>
    ) : null}
  </div>
);

const LookImageTools: React.FC<{
  disabled?: boolean;
  downloading?: boolean;
  downloadOpen?: boolean;
  /** Hide on finalized prior stages; active Look Book keeps regen. */
  showRegenerate?: boolean;
  /** Hide when already in lightbox/expanded view. */
  showExpand?: boolean;
  onRegenerate: () => void;
  onToggleDownload: () => void;
  onDownloadFormat: (format: ImageGeneratorDownloadFormat) => void;
  onCopy: () => void;
  onExpand?: () => void;
}> = ({
  disabled,
  downloading,
  downloadOpen,
  showRegenerate = true,
  showExpand = true,
  onRegenerate,
  onToggleDownload,
  onDownloadFormat,
  onCopy,
  onExpand,
}) => (
  <div className="absolute bottom-2.5 right-2.5 z-10 flex items-center gap-1.5">
    {showRegenerate ? (
      <button
        type="button"
        disabled={disabled}
        onClick={event => {
          event.stopPropagation();
          onRegenerate();
        }}
        className={lookOnImageBtnClassName}
        aria-label="Regenerate"
      >
        <RegenerateIcon className="h-4 w-4" />
      </button>
    ) : null}
    <LookImageDownloadMenu
      disabled={disabled}
      downloading={downloading}
      open={Boolean(downloadOpen)}
      onToggle={onToggleDownload}
      onPick={onDownloadFormat}
    />
    <button
      type="button"
      disabled={disabled}
      onClick={event => {
        event.stopPropagation();
        onCopy();
      }}
      className={lookOnImageBtnClassName}
      aria-label="Copy"
    >
      <CopyIcon className={iconGlyphCompactClassName} />
    </button>
    {showExpand ? (
      <button
        type="button"
        disabled={disabled}
        onClick={event => {
          event.stopPropagation();
          onExpand?.();
        }}
        className={lookOnImageBtnClassName}
        aria-label="Expand"
      >
        <ExpandIcon className={expandGlyphCompactClassName} />
      </button>
    ) : null}
  </div>
);

const LooksRegenChrome: React.FC<{
  looks: CollectionBuildLook[];
  applyMode: RegenApplyMode;
  selectedLookIds: string[];
  faces: LookFace[];
  prompt: string;
  promptTitle: string;
  promptRequired?: boolean;
  disabled?: boolean;
  onApplyAll: () => void;
  onOpenMultiple: () => void;
  onToggleFace: (face: LookFace) => void;
  onRemoveLookId: (id: string) => void;
  onPromptChange: (value: string) => void;
}> = ({
  looks,
  applyMode,
  selectedLookIds,
  faces,
  prompt,
  promptTitle,
  promptRequired = false,
  disabled,
  onApplyAll,
  onOpenMultiple,
  onToggleFace,
  onRemoveLookId,
  onPromptChange,
}) => {
  const selectedLooks = looks.filter(look => selectedLookIds.includes(look.id));
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <p className="text-sm text-secondary">
          {promptTitle || (promptRequired ? "Input" : "Input (optional)")}
        </p>
        <button
          type="button"
          disabled={disabled}
          onClick={onApplyAll}
          className={regenChipClassName(applyMode === "all")}
        >
          Apply to all looks
        </button>
        <span className="h-4 w-px shrink-0 bg-[#D9D9D9]" aria-hidden />
        <button
          type="button"
          disabled={disabled}
          onClick={onOpenMultiple}
          className={regenChipClassName(applyMode === "multiple")}
        >
          Apply to multiple looks
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden
          >
            <path
              d="M3 4.5 6 7.5 9 4.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div
        className={`flex min-h-[7.5rem] overflow-hidden rounded-2xl border ${collectionBuildBorderClassName} bg-white`}
      >
        <div
          className={`flex w-[7.75rem] shrink-0 flex-col gap-2 border-r ${collectionBuildDividerClassName} px-3 py-3`}
        >
          <p className="text-sm text-secondary">Select Image</p>
          <button
            type="button"
            disabled={disabled}
            aria-pressed={faces.includes("front")}
            onClick={() => onToggleFace("front")}
            className={facePillClassName(faces.includes("front"))}
          >
            Front
          </button>
          <button
            type="button"
            disabled={disabled}
            aria-pressed={faces.includes("back")}
            onClick={() => onToggleFace("back")}
            className={facePillClassName(faces.includes("back"))}
          >
            Back
          </button>
        </div>
        <div className="flex min-w-0 flex-1 flex-col px-3 py-3">
          {applyMode === "multiple" && selectedLooks.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {selectedLooks.map(look => (
                <span
                  key={look.id}
                  className="inline-flex h-7 items-center gap-1 rounded-full bg-[#E4D9CF] px-2.5 text-[13px] leading-none text-secondary"
                >
                  {look.number}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onRemoveLookId(look.id)}
                    aria-label={`Remove look ${look.number}`}
                    className="flex h-4 w-4 items-center justify-center rounded-full text-[14px] leading-none text-secondary hover:bg-black/5 focus:outline-none disabled:opacity-40"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <textarea
            value={prompt}
            onChange={event => onPromptChange(event.target.value)}
            disabled={disabled}
            required={promptRequired}
            placeholder=""
            aria-label="Regeneration prompt"
            aria-required={promptRequired || undefined}
            className="min-h-[4.25rem] w-full flex-1 resize-none border-0 bg-transparent p-0 font-avenir text-sm text-secondary focus:outline-none disabled:opacity-60"
          />
        </div>
      </div>
    </div>
  );
};

const lookBookPagerArrowClassName = `flex h-8 w-8 items-center justify-center rounded border ${collectionBuildBorderClassName} bg-white text-lg leading-none text-secondary hover:bg-lightGray focus:outline-none disabled:cursor-not-allowed disabled:opacity-30`;

const lookBookPagerPageClassName = (active: boolean) =>
  `flex h-8 min-w-[2rem] items-center justify-center rounded border px-2 font-avenir text-sm focus:outline-none ${
    active
      ? `${collectionBuildBorderClassName} bg-[#F3EFEA] text-secondary`
      : `${collectionBuildDividerClassName} bg-white text-secondary hover:bg-lightGray`
  }`;

const LookBookPager: React.FC<{
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}> = ({ page, pageCount, onPageChange }) => {
  if (pageCount <= 1) {
    return null;
  }
  return (
    <nav
      aria-label="Look Book pages"
      className="mt-5 flex items-center justify-center gap-1.5"
    >
      <button
        type="button"
        aria-label="Previous look book page"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className={lookBookPagerArrowClassName}
      >
        ‹
      </button>
      {Array.from({ length: pageCount }, (_, index) => index + 1).map(
        pageNumber => (
          <button
            key={pageNumber}
            type="button"
            aria-label={`Look Book page ${pageNumber}`}
            aria-current={pageNumber === page ? "page" : undefined}
            onClick={() => onPageChange(pageNumber)}
            className={lookBookPagerPageClassName(pageNumber === page)}
          >
            {pageNumber}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Next look book page"
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
        className={lookBookPagerArrowClassName}
      >
        ›
      </button>
    </nav>
  );
};

const CollectionBuildResults: React.FC<Props> = ({
  details: detailsProp,
  sessionId: sessionIdProp,
  initialSilhouetteGroups,
  enqueueError,
  onBackToQuery,
}) => {
  const {
    activeSession,
    watchSession,
    loadSession,
    upsertSummaryFields,
    refreshSummaries,
  } = useCollectionBuildSessions();
  const [, setSearchParams] = useSearchParams();
  const persistPayload = (detailsProp.persistPayload ||
    {}) as CollectionBuildSessionPayload;
  const [sessionId, setSessionId] = useState(() =>
    parseCollectionBuildSessionId(sessionIdProp)
  );
  const session =
    activeSession && sessionId && activeSession.sessionId === sessionId
      ? activeSession
      : null;
  const initialFailedMoodboard = collectionBuildFailedOutput(
    session?.outputs,
    "moodboard"
  );
  const initialFailedLook = collectionBuildFailedOutput(
    session?.outputs,
    "look"
  );
  const [details, setDetails] = useState(detailsProp);
  const skipMoodboard = Boolean(details.skipMoodboard);
  const initialGroupsRef = useRef(initialSilhouetteGroups);
  useEffect(() => {
    if (initialSilhouetteGroups?.length) {
      initialGroupsRef.current = initialSilhouetteGroups;
    }
  }, [initialSilhouetteGroups]);
  const [versions, setVersions] = useState<CollectionBuildMoodboardVersion[]>(
    () => moodboardsWithImages(session?.outputs || [])
  );
  const [viewedId, setViewedId] = useState(() => {
    const moodboards = moodboardsWithImages(session?.outputs || []);
    return moodboards[0]?.id || "moodboard-1";
  });
  const [generating, setGenerating] = useState(() => {
    if (skipMoodboard || initialFailedMoodboard) {
      return false;
    }
    if (collectionBuildOutputsPending(session?.outputs, "moodboard")) {
      return true;
    }
    // Session already past moodboard (looks ready/pending) — do not spin moodboard.
    if (
      looksFromRecords(session?.outputs || []).some(look =>
        Boolean(look.versions[0]?.outputId)
      ) ||
      collectionBuildOutputsPending(session?.outputs, "look") ||
      collectionMoodboardsFromOutputs(session?.outputs || []).some(item =>
        Boolean(item.imageUrl)
      )
    ) {
      return false;
    }
    return true;
  });
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [railOpen, setRailOpen] = useState(true);
  const [liked, setLiked] = useState<"up" | "down" | null>(null);
  const [showInput, setShowInput] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [feedbackDetails, setFeedbackDetails] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveNameError, setSaveNameError] = useState("");
  /** Output ids already saved to library this session — one save per generated output. */
  const [savedLibraryOutputIds, setSavedLibraryOutputIds] = useState<
    Set<number>
  >(() => new Set());
  const [saveTargetOutputId, setSaveTargetOutputId] = useState<number | null>(
    null
  );
  const initialSessionLooks = looksFromRecords(
    session?.outputs || [],
    initialSilhouetteGroups
  );
  const hasInitialReadyLooks = initialSessionLooks.some(look =>
    Boolean(look.versions[0]?.outputId)
  );
  const [looksPhase, setLooksPhase] = useState<LooksPhase>(() => {
    if (hasInitialReadyLooks) {
      return "ready";
    }
    if (
      collectionBuildOutputsPending(session?.outputs, "look") ||
      (skipMoodboard && !initialFailedLook)
    ) {
      return "generating";
    }
    return "hidden";
  });
  const [looks, setLooks] = useState<CollectionBuildLook[]>(() =>
    hasInitialReadyLooks ? initialSessionLooks : []
  );
  const [lookBookPage, setLookBookPage] = useState(1);
  const [selectedLookId, setSelectedLookId] = useState(
    () => (hasInitialReadyLooks && initialSessionLooks[0]?.id) || ""
  );
  const [viewedByLookId, setViewedByLookId] = useState<Record<string, string>>(
    () => {
      if (!hasInitialReadyLooks) {
        return {};
      }
      const viewedIds: Record<string, string> = {};
      initialSessionLooks.forEach(look => {
        const versionId = look.versions[0]?.id;
        if (versionId) {
          viewedIds[look.id] = versionId;
        }
      });
      return viewedIds;
    }
  );
  const [looksApproved, setLooksApproved] = useState(() =>
    collectionLooksApproved(session)
  );
  const [sheetsPhase, setSheetsPhase] = useState<SheetsPhase>("hidden");
  const [regenKeys, setRegenKeys] = useState<string[]>([]);
  const [regenOpen, setRegenOpen] = useState(false);
  const [multiSelectOpen, setMultiSelectOpen] = useState(false);
  const [multiSelectPurpose, setMultiSelectPurpose] =
    useState<MultiSelectPurpose>("regen");
  const [applyMode, setApplyMode] = useState<RegenApplyMode>("multiple");
  const [selectedRegenLookIds, setSelectedRegenLookIds] = useState<string[]>(
    []
  );
  const [multiDraftIds, setMultiDraftIds] = useState<string[]>([]);
  const [regenFaces, setRegenFaces] = useState<LookFace[]>([]);
  const [looksPrompt, setLooksPrompt] = useState("");
  const [lightbox, setLightbox] = useState<LightboxTarget | null>(null);
  const [sheetRegenOpen, setSheetRegenOpen] = useState(false);
  const [sheetPrompt, setSheetPrompt] = useState("");
  const [sheetRegenBusy, setSheetRegenBusy] = useState(false);
  const [sheetVersionsByLookId, setSheetVersionsByLookId] = useState<
    Record<string, CollectionBuildSheetVersion[]>
  >({});
  /** Look ids chosen for the design-sheet run (Proceed modal or session hydrate). */
  const [designSheetLookIds, setDesignSheetLookIds] = useState<string[]>(() =>
    designSheetLookIdsFromOutputs(session?.outputs || [])
  );
  const [viewedSheetByLookId, setViewedSheetByLookId] = useState<
    Record<string, string>
  >({});
  /** Selected slide within the current sheet pack (look / apparel / print). */
  const [viewedSheetSlotByLookId, setViewedSheetSlotByLookId] = useState<
    Record<string, string>
  >({});
  const [sheetLiked, setSheetLiked] = useState<"up" | "down" | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const moodboardStageRef = useRef<HTMLElement>(null);
  const looksStageRef = useRef<HTMLElement>(null);
  const designSheetStageRef = useRef<HTMLElement>(null);
  const looksTimerRef = useRef<number>();
  const sheetTimerRef = useRef<number>();
  const looksDownloadRef = useRef({ lookId: "", frontUrl: "", backUrl: "" });
  const moodboardFailToastRef = useRef<number | null>(
    initialFailedMoodboard?.id ?? null
  );
  const looksFailToastRef = useRef<number | null>(
    initialFailedLook?.id ?? null
  );
  const ignoredFailureIdsRef = useRef<Set<number>>(new Set());
  /** Lookbook-approved generation ids — survives poll rebuilds of `looks`. */
  const approvedLookOutputIdsRef = useRef<Set<number>>(new Set());
  /** Track newest moodboard/look/sheet ids so regen poll auto-selects current. */
  const latestMoodboardIdRef = useRef<string | null>(null);
  const latestLookVersionIdRef = useRef<Record<string, string>>({});
  const latestSheetVersionIdRef = useRef<Record<string, string>>({});
  const showInputRef = useRef(false);
  /** Highest stage rank already scrolled into view for this Results mount. */
  const scrolledGenerationStageRankRef = useRef(0);
  const moodboardDownloadRef = useRef<HTMLDivElement>(null);
  const looksHeaderDownloadRef = useRef<HTMLDivElement>(null);
  const sheetHeaderDownloadRef = useRef<HTMLDivElement>(null);
  const sheetToolbarDownloadRef = useRef<HTMLDivElement>(null);
  const [moodboardDownloadOpen, setMoodboardDownloadOpen] = useState(false);
  const [moodboardDownloading, setMoodboardDownloading] = useState(false);
  const [lookDownloadKey, setLookDownloadKey] = useState<string | null>(null);
  const [lookDownloadingKey, setLookDownloadingKey] = useState<string | null>(
    null
  );
  const [looksHeaderDownloadOpen, setLooksHeaderDownloadOpen] = useState(false);
  const [looksHeaderDownloading, setLooksHeaderDownloading] = useState(false);
  const [sheetDownloadOpen, setSheetDownloadOpen] = useState(false);
  const [sheetDownloading, setSheetDownloading] = useState(false);
  const [generationFailure, setGenerationFailure] =
    useState<GenerationFailure | null>(() => {
      const failed = skipMoodboard ? initialFailedLook : initialFailedMoodboard;
      if (!failed) {
        return null;
      }
      const outputType: CollectionBuildGenerationKind = skipMoodboard
        ? "look"
        : "moodboard";
      return {
        ...collectionBuildGenerationErrorCopy(
          outputType,
          failed.details?.errorMessage
        ),
        outputType,
        outputId: failed.id,
      };
    });
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    setDetails(prev => {
      const merged = mergeCollectionBuildDetailsPreferences(
        prev,
        detailsProp.preferences
      );
      const denimMaterialTiles =
        (detailsProp.denimMaterialTiles || []).length > 0
          ? detailsProp.denimMaterialTiles
          : merged.denimMaterialTiles;
      const denimWashTiles =
        (detailsProp.denimWashTiles || []).length > 0
          ? detailsProp.denimWashTiles
          : merged.denimWashTiles;
      const denimMaterialTile =
        detailsProp.denimMaterialTile ||
        denimMaterialTiles?.[0] ||
        merged.denimMaterialTile;
      const denimWashTile =
        detailsProp.denimWashTile ||
        denimWashTiles?.[0] ||
        merged.denimWashTile;
      const microThemes =
        (detailsProp.microThemes || []).length > 0
          ? detailsProp.microThemes
          : merged.microThemes;
      const microTheme =
        detailsProp.microTheme ||
        merged.microTheme ||
        microThemes?.join(", ") ||
        "";
      const materialKey = (denimMaterialTiles || [])
        .map(tile => tile.id)
        .join("\0");
      const washKey = (denimWashTiles || []).map(tile => tile.id).join("\0");
      const mergedMaterialKey = (merged.denimMaterialTiles || [])
        .map(tile => tile.id)
        .join("\0");
      const mergedWashKey = (merged.denimWashTiles || [])
        .map(tile => tile.id)
        .join("\0");
      if (
        denimMaterialTile === merged.denimMaterialTile &&
        denimWashTile === merged.denimWashTile &&
        materialKey === mergedMaterialKey &&
        washKey === mergedWashKey &&
        (detailsProp.wash || merged.wash) === merged.wash &&
        microTheme === merged.microTheme &&
        (merged.microThemes || []).join("\0") === (microThemes || []).join("\0")
      ) {
        return merged;
      }
      return snapshotCollectionBuildDetails({
        ...merged,
        wash: detailsProp.wash || merged.wash,
        denimMaterialTile,
        denimWashTile,
        denimMaterialTiles,
        denimWashTiles,
        microTheme,
        microThemes,
        silhouetteRows: undefined,
      });
    });
  }, [detailsProp]);

  useEffect(() => {
    if (!session) {
      return;
    }
    const sessionPrefs = preferencesFromCollectionSession(
      session.inputSelections,
      session.outputs
    );
    if (!sessionPrefs.length) {
      return;
    }
    setDetails(prev =>
      mergeCollectionBuildDetailsPreferences(prev, sessionPrefs)
    );
  }, [session]);

  const current = generating ? null : versions[0];
  const previous = generating ? versions : versions.slice(1);
  const viewed = generating
    ? null
    : versions.find(item => item.id === viewedId) || current || versions[0];
  const viewedMoodboardApproved = Boolean(viewed?.approvedAt);
  const moodboardGenerationId = moodboardGenerationIdForLooks(
    viewed?.outputId,
    viewed?.approvedAt
  );
  showInputRef.current = showInput;
  const moodboardPreviewItems = useMemo(
    () =>
      versions.map(item => ({
        id: item.id,
        imageUrl: item.imageUrl || null,
      })),
    [versions]
  );
  const moodboardPreviews = useGenerativeResultPreviews(moodboardPreviewItems);
  const lookPreviewItems = useMemo(() => {
    const items: Array<{ id: string; imageUrl: string | null }> = [];
    looks.forEach(look => {
      look.versions.forEach(version => {
        if (!lookVersionPairReady(version)) {
          return;
        }
        if (version.frontUrl) {
          items.push({
            id: `${version.id}:front`,
            imageUrl: version.frontUrl,
          });
        }
        if (version.backUrl) {
          items.push({
            id: `${version.id}:back`,
            imageUrl: version.backUrl,
          });
        }
      });
    });
    return items;
  }, [looks]);
  const lookPreviews = useGenerativeResultPreviews(lookPreviewItems);
  const resolveLookFacePreviewUrl = (
    versionId: string,
    face: LookFace,
    fallbackUrl?: string
  ) => lookPreviews[`${versionId}:${face}`] || fallbackUrl || "";
  const viewedPreviewUrl = viewed
    ? moodboardPreviews[viewed.id] || viewed.imageUrl
    : "";
  const busyMode = versions.length === 0 ? "generate" : "regen";
  const showingDesignSheets =
    (sheetsPhase === "generating" || sheetsPhase === "ready") &&
    looksPhase === "ready";
  const sheetsGenerating = sheetsPhase === "generating";
  const sheetsBusy = sheetsGenerating || sheetRegenBusy;
  const showingLooks = looksPhase !== "hidden" && !showingDesignSheets;
  const looksBusy = looksPhase === "generating";
  const showingError =
    Boolean(generationFailure) && !generating && !looksBusy && !retrying;
  const showingMoodboard =
    !showingError && !skipMoodboard && !showingLooks && !showingDesignSheets;
  /** Ready moodboard URL — keep prior stage when looks / design sheet advance. */
  const moodboardImageUrl =
    viewedPreviewUrl ||
    viewed?.imageUrl ||
    versions.find(item => item.imageUrl)?.imageUrl ||
    "";
  /** Discrete scroll sections: prior stages stay mounted with their own viewport. */
  const showLooksSection = !showingError && looksPhase !== "hidden";
  const showDesignSheetSection = !showingError && showingDesignSheets;
  const showMoodboardSection =
    !showingError &&
    !skipMoodboard &&
    (showingMoodboard ||
      (Boolean(moodboardImageUrl) &&
        (showLooksSection || showDesignSheetSection)));
  const looksRegenBusy = regenKeys.length > 0;
  const lookBookPageCount = getLookBookPageCount(looks.length);
  const sessionReadyForScroll = sessionHasReadyGeneration(session?.outputs);
  const pagedLooks = useMemo(
    () => sliceLookBookPage(looks, lookBookPage),
    [looks, lookBookPage]
  );
  const expectedLookCount = useMemo(() => {
    if (looksRegenBusy) {
      const fromKeys = new Set(
        regenKeys.map(key => key.replace(/:(front|back)$/i, ""))
      ).size;
      return Math.max(
        1,
        fromKeys || selectedRegenLookIds.length || looks.length || 1
      );
    }
    if (looks.length > 0) {
      return looks.length;
    }
    const fromGroups = (initialGroupsRef.current || []).reduce(
      (sum, group) => sum + group.styles.length,
      0
    );
    const fromBreakdown = lookCountFromRangeBreakdown(
      details.rangePlanBreakdown
    );
    return Math.max(
      1,
      fromGroups || fromBreakdown || details.rangePlanTotal || 1
    );
  }, [
    details.rangePlanBreakdown,
    details.rangePlanTotal,
    looks.length,
    looksRegenBusy,
    regenKeys,
    selectedRegenLookIds.length,
  ]);
  const lookBusySlotCount = lookBookPageSlotCount(
    looksBusy || looksRegenBusy
      ? Math.min(LOOK_BOOK_PAGE_SIZE, expectedLookCount)
      : pagedLooks.length
  );
  const lookPageSlotCount = lookBookPageSlotCount(pagedLooks.length);
  const designSheetLooks = useMemo(() => {
    const requestedIds = designSheetLookIds.length
      ? designSheetLookIds
      : designSheetLookIdsFromOutputs(session?.outputs || []);
    const allowedIds = requestedIds.length
      ? requestedIds
      : Object.keys(sheetVersionsByLookId);
    if (!allowedIds.length) {
      return looks;
    }
    const allowed = new Set(allowedIds);
    return looks.filter(look => allowed.has(look.id));
  }, [designSheetLookIds, looks, session?.outputs, sheetVersionsByLookId]);
  useEffect(() => {
    if (!showDesignSheetSection || !designSheetLooks.length) {
      return;
    }
    if (designSheetLooks.some(look => look.id === selectedLookId)) {
      return;
    }
    setSelectedLookId(designSheetLooks[0].id);
  }, [designSheetLooks, selectedLookId, showDesignSheetSection]);
  const selectedLook =
    looks.find(look => look.id === selectedLookId) || looks[0] || null;
  const selectedLookCurrent = selectedLook?.versions[0] || null;
  const selectedLookPrevious = selectedLook?.versions.slice(1) || [];
  const selectedLookViewed = useMemo(() => {
    if (!selectedLook) {
      return null;
    }
    return (
      selectedLook.versions.find(
        version => version.id === viewedByLookId[selectedLook.id]
      ) || selectedLook.versions[0]
    );
  }, [selectedLook, viewedByLookId]);
  const selectedSheetVersions = selectedLook
    ? sheetVersionsByLookId[selectedLook.id] || []
    : [];
  const sheetPreviewItems = useMemo(() => {
    const items: Array<{ id: string; imageUrl: string | null }> = [];
    Object.values(sheetVersionsByLookId).forEach(versionsForLook => {
      versionsForLook.forEach(pack => {
        (pack.slots?.length
          ? pack.slots
          : [
              {
                id: pack.id,
                imageUrl: pack.imageUrl,
              },
            ]
        ).forEach(slot => {
          items.push({
            id: slot.id,
            imageUrl: slot.imageUrl || null,
          });
        });
      });
    });
    return items;
  }, [sheetVersionsByLookId]);
  const sheetPreviews = useGenerativeResultPreviews(sheetPreviewItems);
  const resolveSheetSlotPreviewUrl = (slotId: string, fallbackUrl?: string) =>
    sheetPreviews[slotId] || fallbackUrl || "";
  const selectedSheetCurrent = sheetsBusy
    ? null
    : selectedSheetVersions[0] || null;
  const selectedSheetPrevious = sheetsBusy
    ? selectedSheetVersions
    : selectedSheetVersions.slice(1);
  const selectedSheetViewed =
    selectedSheetVersions.find(
      version => version.id === viewedSheetByLookId[selectedLook?.id || ""]
    ) ||
    selectedSheetVersions[0] ||
    null;
  const selectedSheetSlots = selectedSheetViewed?.slots?.length
    ? selectedSheetViewed.slots
    : selectedSheetViewed
      ? [
          {
            id: selectedSheetViewed.id,
            slotId: "sheet",
            label: "Sheet",
            imageUrl: selectedSheetViewed.imageUrl,
          },
        ]
      : [];
  const selectedSheetSlot =
    selectedSheetSlots.find(
      slot => slot.id === viewedSheetSlotByLookId[selectedLook?.id || ""]
    ) ||
    selectedSheetSlots[0] ||
    null;
  const selectedDesignSheetUrl = selectedSheetSlot
    ? resolveSheetSlotPreviewUrl(
        selectedSheetSlot.id,
        selectedSheetSlot.imageUrl
      )
    : "";
  const viewedSheetId =
    (selectedLook && viewedSheetByLookId[selectedLook.id]) ||
    selectedSheetViewed?.id ||
    "";
  const viewedSheetSlotId = selectedSheetSlot?.id || "";

  const targetedLookIds =
    applyMode === "all" ? looks.map(look => look.id) : selectedRegenLookIds;
  const moodboardGenerationCount = versions.length;
  const moodboardAfterCopy = collectionBuildAfterGenerationCopy(
    moodboardGenerationCount,
    "moodboard"
  );
  const moodboardRegenCopy = collectionBuildRegenPromptCopy(
    moodboardGenerationCount
  );
  const looksGenerationCount = Math.max(
    0,
    ...(targetedLookIds.length
      ? targetedLookIds
      : selectedLookId
        ? [selectedLookId]
        : []
    ).map(
      id =>
        looks
          .find(look => look.id === id)
          ?.versions.filter(version => version.outputId != null).length || 0
    )
  );
  const looksRegenCopy = collectionBuildRegenPromptCopy(looksGenerationCount);
  const canGenerateLooksRegen =
    looksPhase === "ready" &&
    !looksBusy &&
    !looksRegenBusy &&
    targetedLookIds.length > 0 &&
    (!looksRegenCopy.required || Boolean(looksPrompt.trim()));

  if (selectedLookViewed && lookVersionPairReady(selectedLookViewed)) {
    looksDownloadRef.current = {
      lookId: selectedLook?.id || "look",
      frontUrl: resolveLookFacePreviewUrl(
        selectedLookViewed.id,
        "front",
        selectedLookViewed.frontUrl
      ),
      backUrl: resolveLookFacePreviewUrl(
        selectedLookViewed.id,
        "back",
        selectedLookViewed.backUrl
      ),
    };
  } else {
    looksDownloadRef.current = {
      lookId: selectedLook?.id || "look",
      frontUrl: "",
      backUrl: "",
    };
  }

  useEffect(() => {
    const next = parseCollectionBuildSessionId(sessionIdProp);
    if (next) {
      setSessionId(next);
    }
  }, [sessionIdProp]);

  const persistLooks = async (groups: SilhouetteGroup[]) => {
    if (!persistPayload.market || !persistPayload.rangePlan) {
      return;
    }
    const looks = looksPayloadFromGroups(groups);
    if (!looks.length) {
      toast.error("Add at least one complete look before generating.");
      return;
    }
    try {
      const sanitized = sanitizeCollectionBuildSessionPayload(persistPayload);
      const resolvedSessionId = await ensureCollectionBuildSessionId(
        sanitized,
        sessionId
      );
      setSessionId(resolvedSessionId);
      setSearchParams(collectionBuildSessionSearchParams(resolvedSessionId), {
        replace: true,
      });
      const result = await generateCollectionBuildLooks({
        ...sanitized,
        sessionId: resolvedSessionId,
        looks,
        ...(moodboardGenerationId != null ? { moodboardGenerationId } : {}),
      });
      const nextSessionId = result.sessionId || resolvedSessionId;
      setSessionId(nextSessionId);
      watchSession(nextSessionId);
      setSearchParams(collectionBuildSessionSearchParams(nextSessionId), {
        replace: true,
      });
    } catch (error) {
      const message = getCollectionBuildErrorMessage(
        error,
        error instanceof Error
          ? error.message
          : "Unable to start look generation."
      );
      const copy = collectionBuildGenerationErrorCopy("look", message);
      setGenerationFailure({
        ...copy,
        outputType: "look",
        outputId: null,
      });
      setLooksPhase("hidden");
      toast.error(copy.toast);
    }
  };

  const applyLooks = useCallback(
    (nextLooks: CollectionBuildLook[], silent = false) => {
      if (!nextLooks.length) {
        setLooksPhase("hidden");
        toast.error("Could not create looks from this range plan.");
        return;
      }
      const normalizedLooks = withSequentialLookNumbers(nextLooks);
      const viewedIds: Record<string, string> = {};
      normalizedLooks.forEach(look => {
        const versionId = look.versions[0]?.id;
        if (versionId) {
          viewedIds[look.id] = versionId;
          latestLookVersionIdRef.current[look.id] = versionId;
        }
      });
      setLooks(normalizedLooks);
      setLookBookPage(1);
      setSelectedLookId(normalizedLooks[0].id);
      setViewedByLookId(viewedIds);
      setLooksPhase("ready");
      if (
        !silent &&
        normalizedLooks.every(look => lookVersionPairReady(look.versions[0]))
      ) {
        toast.success("Looks generated.");
      }
    },
    []
  );

  const applyGeneratedLooks = useCallback(
    (sourceGroups: SilhouetteGroup[]) => {
      const fromSession = session
        ? looksFromRecords(session.outputs, sourceGroups)
        : [];
      if (fromSession.length) {
        applyLooks(fromSession);
        return;
      }
      if (collectionBuildFailedOutput(session?.outputs, "look")) {
        return;
      }
      if (!session) {
        applyLooks(createLooksFromGroups(sourceGroups));
      }
    },
    [applyLooks, session]
  );

  const noteGenerationFailure = useCallback(
    (
      outputType: CollectionBuildGenerationKind,
      errorMessage?: string | null,
      outputId?: number | null,
      notify = true
    ) => {
      if (outputId && ignoredFailureIdsRef.current.has(outputId)) {
        return;
      }
      const copy = collectionBuildGenerationErrorCopy(outputType, errorMessage);
      const toastRef =
        outputType === "moodboard" ? moodboardFailToastRef : looksFailToastRef;
      const nextId = outputId ?? -1;
      setGenerationFailure({
        ...copy,
        outputType,
        outputId: outputId ?? null,
      });
      if (outputType === "moodboard") {
        setGenerating(false);
      } else {
        setLooksPhase("hidden");
      }
      if (notify && toastRef.current !== nextId) {
        toastRef.current = nextId;
        toast.error(copy.toast);
      }
    },
    []
  );

  useEffect(() => {
    const outputs = session?.outputs || [];
    const sessionLooks = session ? looksFromRecords(outputs) : [];
    const sessionMoodboards = session ? moodboardsWithImages(outputs) : [];
    if (
      sessionLooks.length &&
      sessionLooks.some(look => look.versions[0]?.outputId)
    ) {
      applyLooks(sessionLooks, true);
      const sheets = session ? collectionSheetsFromOutputs(outputs) : {};
      if (Object.keys(sheets).length) {
        setSheetVersionsByLookId(sheets);
        setViewedSheetByLookId(prev => {
          const next = { ...prev };
          Object.entries(sheets).forEach(([lookId, packs]) => {
            if (!next[lookId] && packs[0]?.id) {
              next[lookId] = packs[0].id;
            }
          });
          return next;
        });
        setViewedSheetSlotByLookId(prev => {
          const next = { ...prev };
          Object.entries(sheets).forEach(([lookId, packs]) => {
            const pack = packs[0];
            const firstSlot = pack?.slots?.[0]?.id;
            if (!firstSlot) {
              return;
            }
            if (!(pack?.slots || []).some(slot => slot.id === next[lookId])) {
              next[lookId] = firstSlot;
            }
          });
          return next;
        });
        setSheetsPhase("ready");
        setLooksApproved(true);
      } else {
        setLooksApproved(collectionLooksApproved(session));
      }
      setGenerating(false);
      return undefined;
    }
    if (sessionMoodboards.length) {
      setVersions(sessionMoodboards);
      latestMoodboardIdRef.current = sessionMoodboards[0].id;
      setViewedId(sessionMoodboards[0].id);
      setGenerating(false);
      return undefined;
    }
    const failedMoodboard = collectionBuildFailedOutput(outputs, "moodboard");
    if (failedMoodboard) {
      noteGenerationFailure(
        "moodboard",
        failedMoodboard.details?.errorMessage,
        failedMoodboard.id,
        false
      );
      return undefined;
    }
    const failedLook = collectionBuildFailedOutput(outputs, "look");
    if (failedLook && !collectionBuildOutputsPending(outputs, "look")) {
      noteGenerationFailure(
        "look",
        failedLook.details?.errorMessage,
        failedLook.id,
        false
      );
      return undefined;
    }
    if (skipMoodboard) {
      if (session || sessionId) {
        setLooksPhase("generating");
        return undefined;
      }
      const sourceGroups =
        initialGroupsRef.current && initialGroupsRef.current.length > 0
          ? initialGroupsRef.current
          : createSilhouetteGroups(details.rangePlanBreakdown || []);
      if (
        !areSilhouetteGroupsComplete(sourceGroups, {
          category: details.category,
          market: details.market,
          startingDirection: details.startingDirection,
        })
      ) {
        setLooksPhase("hidden");
        toast.error("Could not create looks from this range plan.");
        return undefined;
      }
      window.clearTimeout(looksTimerRef.current);
      looksTimerRef.current = window.setTimeout(() => {
        applyGeneratedLooks(sourceGroups);
      }, MOCK_GENERATE_DELAY_MS);
      return () => window.clearTimeout(looksTimerRef.current);
    }
    // Wait for session poll — server owns moodboard generation.
    return undefined;
  }, []);

  useEffect(() => {
    if (!session) {
      return;
    }
    const outputs = session.outputs || [];
    const moodboards = moodboardsWithImages(outputs);
    const moodboardPending = collectionBuildOutputsPending(
      outputs,
      "moodboard"
    );
    if (moodboards.length) {
      const latestId = moodboards[0].id;
      setVersions(moodboards);
      // When a newer moodboard arrives (regen / first ready), show it immediately.
      if (latestMoodboardIdRef.current !== latestId) {
        latestMoodboardIdRef.current = latestId;
        setViewedId(latestId);
      } else {
        setViewedId(prev =>
          moodboards.some(item => item.id === prev) ? prev : latestId
        );
      }
      // Regen only: ready images exist while a new moodboard is still pending.
      // First generate has no images yet and uses the branch below — leave it alone.
      if (moodboardPending) {
        setGenerating(true);
        setGenerationFailure(prev =>
          prev?.outputType === "moodboard" ? null : prev
        );
      } else {
        setGenerating(false);
        setGenerationFailure(prev =>
          prev?.outputType === "moodboard" ? null : prev
        );
        moodboardFailToastRef.current = null;
      }
    } else if (moodboardPending) {
      setGenerating(true);
      setGenerationFailure(prev =>
        prev?.outputType === "moodboard" ? null : prev
      );
    } else {
      const failedMoodboard = collectionBuildFailedOutput(outputs, "moodboard");
      const staleFailure = Boolean(
        failedMoodboard && ignoredFailureIdsRef.current.has(failedMoodboard.id)
      );
      if (failedMoodboard && !retrying && !staleFailure) {
        noteGenerationFailure(
          "moodboard",
          failedMoodboard.details?.errorMessage,
          failedMoodboard.id
        );
      } else if (retrying || staleFailure) {
        setGenerating(true);
        setGenerationFailure(prev =>
          prev?.outputType === "moodboard" ? null : prev
        );
      }
    }
    const nextLooks = looksFromRecords(outputs, initialGroupsRef.current);
    if (nextLooks.some(look => look.versions[0]?.outputId)) {
      seedApprovedLookOutputIds(outputs, approvedLookOutputIdsRef.current);
      setLooks(prev => mergeCollectionLookApprovals(prev, nextLooks));
      setSelectedLookId(prev =>
        nextLooks.some(look => look.id === prev) ? prev : nextLooks[0].id
      );
      setViewedByLookId(prev => {
        const next = { ...prev };
        nextLooks.forEach(look => {
          const versionId = look.versions[0]?.id;
          if (!versionId) {
            return;
          }
          const priorLatest = latestLookVersionIdRef.current[look.id];
          const stillValid = look.versions.some(
            version => version.id === next[look.id]
          );
          // New current version (regen) → auto-select; else keep valid history pick.
          if (priorLatest !== versionId || !stillValid || !next[look.id]) {
            latestLookVersionIdRef.current[look.id] = versionId;
            next[look.id] = versionId;
          }
        });
        return next;
      });
      setLooksPhase("ready");
      setGenerationFailure(prev => (prev?.outputType === "look" ? null : prev));
      looksFailToastRef.current = null;
    } else if (collectionBuildOutputsPending(outputs, "look")) {
      setLooksPhase("generating");
      setGenerationFailure(prev => (prev?.outputType === "look" ? null : prev));
    } else {
      const failedLook = collectionBuildFailedOutput(outputs, "look");
      const staleLookFailure = Boolean(
        failedLook && ignoredFailureIdsRef.current.has(failedLook.id)
      );
      if (failedLook && !retrying && !staleLookFailure) {
        noteGenerationFailure(
          "look",
          failedLook.details?.errorMessage,
          failedLook.id
        );
      } else if (retrying || staleLookFailure) {
        setLooksPhase("generating");
        setGenerationFailure(prev =>
          prev?.outputType === "look" ? null : prev
        );
      }
    }
    const sheets = collectionSheetsFromOutputs(outputs);
    const sheetGenerating = (outputs || []).some(
      item =>
        item.outputType === "look" &&
        item.details?.designSheet?.status === "generating"
    );
    const sheetFailed = (outputs || []).some(
      item =>
        item.outputType === "look" &&
        item.details?.designSheet?.status === "failed"
    );
    if (Object.keys(sheets).length) {
      setSheetVersionsByLookId(sheets);
      setViewedSheetByLookId(prev => {
        const next = { ...prev };
        Object.entries(sheets).forEach(([lookId, versions]) => {
          const versionId = versions[0]?.id;
          if (!versionId) {
            return;
          }
          const priorLatest = latestSheetVersionIdRef.current[lookId];
          const stillValid = versions.some(
            version => version.id === next[lookId]
          );
          if (priorLatest !== versionId || !stillValid || !next[lookId]) {
            latestSheetVersionIdRef.current[lookId] = versionId;
            next[lookId] = versionId;
          }
        });
        return next;
      });
      setViewedSheetSlotByLookId(prev => {
        const next = { ...prev };
        Object.entries(sheets).forEach(([lookId, packs]) => {
          const packId =
            latestSheetVersionIdRef.current[lookId] || packs[0]?.id;
          const pack =
            packs.find(item => item.id === packId) || packs[0] || null;
          const firstSlotId = pack?.slots?.[0]?.id;
          if (!firstSlotId) {
            return;
          }
          const stillValid = (pack?.slots || []).some(
            slot => slot.id === next[lookId]
          );
          if (!stillValid) {
            next[lookId] = firstSlotId;
          }
        });
        return next;
      });
      if (sheetsPhase === "generating" && !sheetGenerating) {
        setSheetsPhase("ready");
        setSheetRegenBusy(false);
        setSheetRegenOpen(false);
      }
    } else if (sheetGenerating && sheetsPhase === "generating") {
      setSheetsPhase("generating");
    } else if (sheetFailed && sheetsPhase === "generating") {
      setSheetsPhase("ready");
      setSheetRegenBusy(false);
      toast.error("One or more design sheets failed. Try again on that look.");
    }
    if (
      Object.keys(sheets).length ||
      sheetGenerating ||
      sheetsPhase === "generating" ||
      sheetsPhase === "ready"
    ) {
      setLooksApproved(true);
    } else {
      setLooksApproved(collectionLooksApproved(session));
    }
  }, [noteGenerationFailure, retrying, session, skipMoodboard]);

  useEffect(() => {
    if (!enqueueError) {
      return;
    }
    noteGenerationFailure(
      skipMoodboard ? "look" : "moodboard",
      enqueueError,
      null,
      false
    );
    setRetrying(false);
  }, [enqueueError, noteGenerationFailure, skipMoodboard]);

  const rememberSessionGenerating = (id: number) => {
    if (Number.isInteger(id) && id > 0) {
      upsertSummaryFields(id, { status: "generating" });
      void refreshSummaries();
    }
  };

  const handleRetryGeneration = async () => {
    if (retrying) {
      return;
    }
    if (!persistPayload.market || !persistPayload.rangePlan) {
      toast.error(
        "Missing collection selections. Go back to the query to retry."
      );
      return;
    }
    const outputType: CollectionBuildGenerationKind =
      generationFailure?.outputType || (skipMoodboard ? "look" : "moodboard");
    (session?.outputs || []).forEach(item => {
      if (item.outputType === outputType && item.status === "failed") {
        ignoredFailureIdsRef.current.add(item.id);
      }
    });
    if (generationFailure?.outputId) {
      ignoredFailureIdsRef.current.add(generationFailure.outputId);
    }
    setRetrying(true);
    setGenerationFailure(null);
    moodboardFailToastRef.current = null;
    looksFailToastRef.current = null;
    if (outputType === "moodboard") {
      setGenerating(true);
    } else {
      setLooksPhase("generating");
    }
    try {
      const sanitized = sanitizeCollectionBuildSessionPayload(persistPayload);
      const resolvedSessionId = await ensureCollectionBuildSessionId(
        sanitized,
        sessionId
      );
      setSessionId(resolvedSessionId);
      rememberSessionGenerating(resolvedSessionId);
      setSearchParams(collectionBuildSessionSearchParams(resolvedSessionId), {
        replace: true,
      });
      if (outputType === "moodboard") {
        const record = await generateCollectionBuildMoodboard({
          ...sanitized,
          sessionId: resolvedSessionId,
        });
        const nextSessionId = record.sessionId || resolvedSessionId;
        setSessionId(nextSessionId);
        rememberSessionGenerating(nextSessionId);
        // Watch only after enqueue so the first GET includes the new generating
        // row. Watching the failed session first re-toasts timeout and drops poll.
        watchSession(nextSessionId);
        if (nextSessionId !== resolvedSessionId) {
          setSearchParams(collectionBuildSessionSearchParams(nextSessionId), {
            replace: true,
          });
        }
        return;
      }
      const fromGroups = initialGroupsRef.current?.length
        ? looksPayloadFromGroups(initialGroupsRef.current)
        : [];
      const fromPersist = Array.isArray(persistPayload.looks)
        ? persistPayload.looks
        : [];
      const looks = fromGroups.length
        ? fromGroups
        : fromPersist.length
          ? fromPersist
          : looksPayloadFromSessionOutputs(session?.outputs);
      if (!looks.length) {
        toast.error(
          "Look selections are missing. Go back to the query to choose silhouettes."
        );
        setLooksPhase("hidden");
        ignoredFailureIdsRef.current.clear();
        upsertSummaryFields(resolvedSessionId, { status: "failed" });
        noteGenerationFailure(
          "look",
          "Look selections are missing. Go back to the query to choose silhouettes."
        );
        return;
      }
      setLooksPhase("generating");
      const result = await generateCollectionBuildLooks({
        ...sanitized,
        sessionId: resolvedSessionId,
        looks,
        ...(moodboardGenerationId != null ? { moodboardGenerationId } : {}),
      });
      const nextSessionId = result.sessionId || resolvedSessionId;
      setSessionId(nextSessionId);
      rememberSessionGenerating(nextSessionId);
      watchSession(nextSessionId);
      if (nextSessionId !== resolvedSessionId) {
        setSearchParams(collectionBuildSessionSearchParams(nextSessionId), {
          replace: true,
        });
      }
    } catch (error) {
      const message = getCollectionBuildErrorMessage(
        error,
        outputType === "moodboard"
          ? "Unable to start moodboard generation."
          : "Unable to start look generation."
      );
      ignoredFailureIdsRef.current.clear();
      if (sessionId) {
        upsertSummaryFields(sessionId, { status: "failed" });
      }
      noteGenerationFailure(outputType, message);
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    setMoodboardDownloadOpen(false);
  }, [viewedId, generating]);

  useEffect(() => {
    if (
      !moodboardDownloadOpen &&
      !looksHeaderDownloadOpen &&
      !sheetDownloadOpen &&
      !lookDownloadKey
    ) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        moodboardDownloadOpen &&
        moodboardDownloadRef.current &&
        !moodboardDownloadRef.current.contains(target)
      ) {
        setMoodboardDownloadOpen(false);
      }
      if (
        looksHeaderDownloadOpen &&
        looksHeaderDownloadRef.current &&
        !looksHeaderDownloadRef.current.contains(target)
      ) {
        setLooksHeaderDownloadOpen(false);
      }
      if (
        sheetDownloadOpen &&
        !(
          sheetHeaderDownloadRef.current?.contains(target) ||
          sheetToolbarDownloadRef.current?.contains(target)
        )
      ) {
        setSheetDownloadOpen(false);
      }
      if (lookDownloadKey) {
        const node = event.target as Element | null;
        if (!node?.closest?.("[data-look-download]")) {
          setLookDownloadKey(null);
        }
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoodboardDownloadOpen(false);
        setLooksHeaderDownloadOpen(false);
        setSheetDownloadOpen(false);
        setLookDownloadKey(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [
    lookDownloadKey,
    looksHeaderDownloadOpen,
    moodboardDownloadOpen,
    sheetDownloadOpen,
  ]);

  useEffect(() => {
    if (!showInput && !regenOpen && !sheetRegenOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      if (showInput) {
        setShowInput(false);
      }
      if (regenOpen) {
        setRegenOpen(false);
      }
      if (sheetRegenOpen) {
        setSheetRegenOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [regenOpen, sheetRegenOpen, showInput]);

  // Keep page in range when the look set shrinks (e.g. fewer results).
  // Do not reset to page 1 on every length bump — that kicked users off
  // page 2+ while looks streamed in (8 → 9 → … → 20).
  useEffect(() => {
    setLookBookPage(current =>
      Math.min(Math.max(1, current), lookBookPageCount)
    );
  }, [lookBookPageCount]);

  // Rail / regen / design-sheet tabs use global look ids — keep the grid
  // on the page that contains the selected look (Look 1..N, not page-local).
  useEffect(() => {
    if (!selectedLookId || !looks.length) {
      return;
    }
    const index = looks.findIndex(look => look.id === selectedLookId);
    if (index < 0) {
      return;
    }
    const pageForLook = getLookBookPageForIndex(index);
    setLookBookPage(current =>
      current === pageForLook ? current : pageForLook
    );
  }, [selectedLookId, looks]);

  // Scroll to the furthest generation stage once per open / first appear.
  // Prefer design sheet > looks > moodboard. Rank guard stops re-hijack when
  // the user browses prior stages; sessionReady is a stable boolean (not session).
  useEffect(() => {
    let stage: LatestGenerationStage | null = null;
    if (showingDesignSheets) {
      stage = "design-sheet";
    } else if (showingLooks) {
      stage = "looks";
    } else if (showingError) {
      stage = generationFailure?.outputType === "look" ? "looks" : "moodboard";
    } else if (showingMoodboard) {
      if (versions.length > 0) {
        // Hydrated or completed moodboard image is on screen.
        stage = "moodboard";
      } else if (generating && !sessionReadyForScroll) {
        // Fresh generate: results UI just appeared (busy), no ready outputs yet.
        stage = "moodboard";
      }
      // else: reopen still applying session outputs into local state — wait.
    }
    if (!stage) {
      return;
    }
    const rank = LATEST_GENERATION_STAGE_RANK[stage];
    if (rank <= scrolledGenerationStageRankRef.current) {
      return;
    }
    scrolledGenerationStageRankRef.current = rank;
    const frame = window.requestAnimationFrame(() => {
      const target =
        stage === "design-sheet"
          ? designSheetStageRef.current
          : stage === "looks"
            ? looksStageRef.current
            : moodboardStageRef.current;
      scrollCollectionSectionIntoView(target || rootRef.current, "start");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    generating,
    generationFailure?.outputType,
    sessionReadyForScroll,
    showingDesignSheets,
    showingError,
    showingLooks,
    showingMoodboard,
    versions.length,
  ]);

  useEffect(
    () => () => {
      window.clearTimeout(looksTimerRef.current);
      window.clearTimeout(sheetTimerRef.current);
    },
    []
  );

  const viewedVersionFor = (
    look: CollectionBuildLook
  ): CollectionBuildLookVersion | undefined =>
    look.versions.find(version => version.id === viewedByLookId[look.id]) ||
    look.versions[0];

  const handleLookBookPageChange = (page: number) => {
    const nextPage = Math.min(Math.max(1, page), lookBookPageCount);
    setLookBookPage(nextPage);
    const pageLooks = sliceLookBookPage(looks, nextPage);
    if (pageLooks[0]) {
      setSelectedLookId(pageLooks[0].id);
    }
  };

  const toggleRegenInput = () => {
    setShowInput(open => !open);
  };

  const handleApprove = async () => {
    if (generating || !viewed?.outputId) {
      return;
    }
    const outputId = viewed.outputId;
    try {
      await approveCollectionBuild(outputId);
      if (sessionId) {
        await loadSession(sessionId);
      }
    } catch (error) {
      toast.error(
        getCollectionBuildErrorMessage(error, "Unable to approve moodboard.")
      );
      return;
    }
    const approvedAt = new Date().toISOString();
    setVersions(prev =>
      prev.map(item =>
        item.outputId === outputId ? { ...item, approvedAt } : item
      )
    );
    setShowInput(false);
    toast.success("Moodboard approved.");
  };

  const handleGenerate = () => {
    if (generating) {
      return;
    }
    if (moodboardRegenCopy.required && !prompt.trim()) {
      return;
    }
    const sourceId = viewed?.outputId || versions[0]?.outputId;
    if (!sourceId) {
      toast.error("No moodboard to regenerate yet.");
      return;
    }
    setGenerating(true);
    setLiked(null);
    setShowInput(false);
    void regenerateCollectionBuild(sourceId, {
      additionalPrompt: prompt.trim() || undefined,
    })
      .then(record => {
        if (record.sessionId) {
          setSessionId(record.sessionId);
          watchSession(record.sessionId);
        }
      })
      .catch(error => {
        setGenerating(false);
        toast.error(
          getCollectionBuildErrorMessage(
            error,
            "Unable to regenerate moodboard."
          )
        );
      });
  };

  const handleMoodboardDownload = async (
    format: ImageGeneratorDownloadFormat
  ) => {
    if (!viewed || moodboardDownloading) {
      return;
    }
    // svg_hybrid: prefer persisted structured SVG; AI / older boards keep PNG wrap.
    const src =
      format === "svg" && viewed.svgUrl
        ? viewed.svgUrl
        : viewedPreviewUrl || viewed.imageUrl;
    if (!src) {
      return;
    }
    if (format === "svg" && !viewed.svgUrl) {
      toast(
        "This board has no structured SVG — download will be a PNG wrapper (not editable in Figma). Generate a new moodboard with the svg_hybrid engine for editable text/chips."
      );
    }
    setMoodboardDownloadOpen(false);
    setMoodboardDownloading(true);
    try {
      await downloadImageGeneratorLook(src, viewed.id || "moodboard", format);
    } catch {
      toast.error("Unable to download this image.");
    } finally {
      setMoodboardDownloading(false);
    }
  };

  const handleLookFaceDownload = async (
    lookId: string,
    face: LookFace,
    url: string,
    format: ImageGeneratorDownloadFormat
  ) => {
    const key = lookFaceKey(lookId, face);
    if (!url || lookDownloadingKey) {
      return;
    }
    setLookDownloadKey(null);
    setLookDownloadingKey(key);
    try {
      await downloadImageGeneratorLook(url, `${lookId}-${face}`, format);
    } catch {
      toast.error("Unable to download this image.");
    } finally {
      setLookDownloadingKey(null);
    }
  };

  const handleLooksHeaderDownload = async (
    format: ImageGeneratorDownloadFormat
  ) => {
    const { lookId, frontUrl, backUrl } = looksDownloadRef.current;
    if (looksHeaderDownloading || (!frontUrl && !backUrl)) {
      return;
    }
    setLooksHeaderDownloadOpen(false);
    setLooksHeaderDownloading(true);
    try {
      if (frontUrl) {
        await downloadImageGeneratorLook(frontUrl, `${lookId}-front`, format);
      }
      if (backUrl) {
        await downloadImageGeneratorLook(backUrl, `${lookId}-back`, format);
      }
    } catch {
      toast.error("Unable to download this image.");
    } finally {
      setLooksHeaderDownloading(false);
    }
  };

  const handleCopy = async () => {
    if (!viewed) {
      return;
    }
    try {
      await navigator.clipboard.writeText(viewed.imageUrl);
      toast.success("Image link copied.");
    } catch {
      toast.error("Unable to copy.");
    }
  };

  const copyLookImage = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Image link copied.");
    } catch {
      toast.error("Unable to copy.");
    }
  };

  const outputIdForFeedback = () => {
    if (showingDesignSheets || showingLooks) {
      return selectedLookViewed?.outputId;
    }
    return viewed?.outputId;
  };

  const viewForFeedback = (): ImageGeneratorLookView => {
    if (showingLooks && !showingDesignSheets) {
      return lightbox?.face || "front";
    }
    return "single";
  };

  const openFeedback = (value: "up" | "down") => {
    setLiked(value);
    setFeedbackReason("");
    setFeedbackDetails("");
    setFeedbackOpen(true);
    const outputId = outputIdForFeedback();
    if (!outputId) {
      return;
    }
    void setCollectionBuildLike(outputId, viewForFeedback(), value === "up")
      .then(record => {
        if (record.sessionId) {
          void loadSession(record.sessionId);
        }
      })
      .catch(error => {
        toast.error(
          getCollectionBuildErrorMessage(error, "Unable to save rating.")
        );
      });
  };

  const handleFeedbackSubmit = () => {
    const outputId = outputIdForFeedback();
    if (outputId) {
      void submitCollectionBuildFeedback(outputId, {
        view: viewForFeedback(),
        reason: toFeedbackReason(feedbackReason),
        details: feedbackDetails.trim() || undefined,
      })
        .then(record => {
          if (record.sessionId) {
            void loadSession(record.sessionId);
          }
        })
        .catch(error => {
          toast.error(
            getCollectionBuildErrorMessage(error, "Unable to save feedback.")
          );
        });
    }
    toast.success("Feedback submitted.");
    setFeedbackOpen(false);
  };

  const closeSaveModal = () => {
    setSaveOpen(false);
    setSaveName("");
    setSaveNameError("");
    setSaveTargetOutputId(null);
  };

  const openSaveModal = (outputId: number | null | undefined) => {
    if (outputId == null || savedLibraryOutputIds.has(outputId)) {
      return;
    }
    setSaveName("");
    setSaveNameError("");
    setSaveTargetOutputId(outputId);
    setSaveOpen(true);
  };

  const handleSaveToLibrary = () => {
    const trimmed = saveName.trim();
    if (!trimmed) {
      setSaveNameError("A name is required.");
      return;
    }
    if (
      saveTargetOutputId == null ||
      savedLibraryOutputIds.has(saveTargetOutputId)
    ) {
      closeSaveModal();
      return;
    }
    // No CB library API yet — block repeat saves client-side by output id.
    setSavedLibraryOutputIds(prev => {
      const next = new Set(prev);
      next.add(saveTargetOutputId);
      return next;
    });
    toast.success("Saved to library.");
    closeSaveModal();
  };

  const moodboardLibrarySaved =
    viewed?.outputId != null && savedLibraryOutputIds.has(viewed.outputId);
  const looksLibrarySaved =
    selectedLookViewed?.outputId != null &&
    savedLibraryOutputIds.has(selectedLookViewed.outputId);

  const resetLooksRegenForm = () => {
    setApplyMode("multiple");
    setSelectedRegenLookIds([]);
    setMultiDraftIds([]);
    setRegenFaces([]);
    setLooksPrompt("");
    setMultiSelectOpen(false);
  };

  const resolveLookSourceGroups = (): SilhouetteGroup[] => {
    if (initialGroupsRef.current && initialGroupsRef.current.length > 0) {
      return initialGroupsRef.current;
    }
    const payload = (details.persistPayload ||
      {}) as CollectionBuildSessionPayload;
    const seedLooks = payload.looks;
    if (seedLooks?.length) {
      return applyLooksToSilhouetteGroups(
        createSilhouetteGroups(details.rangePlanBreakdown || []),
        seedLooks,
        {
          garmentFormat: payload.garmentFormat,
          patternFormat: payload.patternFormat,
          embroideryType: payload.embroideryType,
        }
      );
    }
    return createSilhouetteGroups(details.rangePlanBreakdown || []);
  };

  const handleGenerateLook = (groups: SilhouetteGroup[]) => {
    if (looksPhase === "generating") {
      return;
    }
    const sourceGroups = groups.length > 0 ? groups : resolveLookSourceGroups();
    if (
      !areSilhouetteGroupsComplete(sourceGroups, {
        category: details.category,
        market: details.market,
        startingDirection: details.startingDirection,
      })
    ) {
      toast.error(
        "Look selections are missing. Go back to the query to choose silhouettes."
      );
      return;
    }
    setSheetsPhase("hidden");
    setLooksPhase("generating");
    setLooks([]);
    setLookBookPage(1);
    setLooksApproved(false);
    setSelectedLookId("");
    setViewedByLookId({});
    latestLookVersionIdRef.current = {};
    latestSheetVersionIdRef.current = {};
    setRegenKeys([]);
    setRegenOpen(false);
    setLightbox(null);
    setSheetRegenOpen(false);
    setSheetPrompt("");
    setSheetRegenBusy(false);
    setSheetVersionsByLookId({});
    setViewedSheetByLookId({});
    setViewedSheetSlotByLookId({});
    setSheetLiked(null);
    resetLooksRegenForm();
    window.clearTimeout(sheetTimerRef.current);
    window.clearTimeout(looksTimerRef.current);
    void persistLooks(sourceGroups);
    if (!sessionId) {
      looksTimerRef.current = window.setTimeout(() => {
        applyGeneratedLooks(sourceGroups);
      }, MOCK_GENERATE_DELAY_MS);
    }
  };

  const handleProceedToLooks = () => {
    if (!viewedMoodboardApproved || looksPhase === "generating") {
      return;
    }
    handleGenerateLook(resolveLookSourceGroups());
  };

  /** Ready/current look generation id for design-sheet start (approval is lookbook-level). */
  const resolveLookSheetOutputId = (
    look: CollectionBuildLook
  ): number | null => {
    const preferred =
      look.versions.find(version => lookVersionPairReady(version)) ||
      look.versions[0];
    const fromVersion =
      toCollectionBuildOutputId(preferred?.outputId) ??
      toCollectionBuildOutputId(preferred?.id);
    if (fromVersion != null) {
      return fromVersion;
    }
    return resolveCollectionBuildApprovedSheetOutputId(look, session?.outputs);
  };

  /** Approve every ready look in the lookbook; sheet modal only picks which ones to sheet. */
  const handleApproveLooks = async () => {
    if (looksPhase !== "ready" || !looks.length) {
      return;
    }
    const targets: number[] = [];
    looks.forEach(look => {
      const version =
        (look.id === selectedLook?.id &&
        lookVersionPairReady(selectedLookViewed)
          ? selectedLookViewed
          : null) ||
        look.versions.find(item => lookVersionPairReady(item)) ||
        look.versions[0];
      const outputId =
        toCollectionBuildOutputId(version?.outputId) ??
        toCollectionBuildOutputId(version?.id);
      if (outputId) {
        targets.push(outputId);
      }
    });
    if (!targets.length) {
      toast.error("No look to approve yet.");
      return;
    }
    let approvedAt = new Date().toISOString();
    try {
      for (const outputId of targets) {
        const record = await approveCollectionBuild(outputId);
        approvedAt = record.review?.approvedAt || approvedAt;
        approvedLookOutputIdsRef.current.add(outputId);
      }
    } catch (error) {
      toast.error(
        getCollectionBuildErrorMessage(error, "Unable to approve lookbook.")
      );
      return;
    }
    const approvedIds = new Set(targets);
    const markLookbookApproved = (prev: CollectionBuildLook[]) => {
      const next = prev.map(look => ({
        ...look,
        versions: look.versions.map(version => {
          const versionOutputId =
            toCollectionBuildOutputId(version.outputId) ??
            toCollectionBuildOutputId(version.id);
          return versionOutputId != null && approvedIds.has(versionOutputId)
            ? { ...version, outputId: versionOutputId, approvedAt }
            : version;
        }),
      }));
      setLooksApproved(true);
      return next;
    };
    setLooks(markLookbookApproved);
    if (sessionId) {
      await loadSession(sessionId);
      setLooks(markLookbookApproved);
    }
    setRegenOpen(false);
    setMultiSelectOpen(false);
    toast.success("Lookbook approved.");
  };

  const lookbookApprovedForSheets =
    looksApproved ||
    looks.some(look =>
      look.versions.some(version => Boolean(version.approvedAt))
    ) ||
    approvedLookOutputIdsRef.current.size > 0 ||
    collectionLooksApproved(session);

  const handleProceedToDesignSheet = () => {
    if (looksPhase !== "ready" || !looks.length || sheetsPhase !== "hidden") {
      return;
    }
    if (!lookbookApprovedForSheets) {
      toast.error("Approve the lookbook before design sheets.");
      return;
    }
    // Popup selects which looks from the approved lookbook get sheets.
    setMultiSelectPurpose("design-sheet");
    setMultiDraftIds([]);
    setMultiSelectOpen(true);
    setRegenOpen(false);
    setLightbox(null);
  };

  const handleConfirmGenerateDesignSheets = (lookIds: string[]) => {
    if (looksPhase !== "ready" || sheetsPhase !== "hidden") {
      return;
    }
    if (!lookbookApprovedForSheets) {
      toast.error("Approve the lookbook before design sheets.");
      return;
    }
    const selectedLooks = looks.filter(look => lookIds.includes(look.id));
    if (!selectedLooks.length) {
      toast.error("Select at least one look for design sheets.");
      return;
    }
    const sheetIds = selectedLooks
      .map(look => resolveLookSheetOutputId(look))
      .filter((id): id is number => id != null);
    if (!sheetIds.length || sheetIds.length !== selectedLooks.length) {
      toast.error("Could not resolve look outputs for design sheets.");
      return;
    }
    const requestedIds = selectedLooks.map(look => look.id);
    setDesignSheetLookIds(requestedIds);
    setSelectedLookId(prev =>
      requestedIds.includes(prev) ? prev : requestedIds[0]
    );
    setMultiSelectOpen(false);
    setSheetsPhase("generating");
    setRegenOpen(false);
    setLightbox(null);
    setSheetRegenOpen(false);
    setSheetPrompt("");
    setSheetLiked(null);
    setSheetRegenBusy(false);
    window.clearTimeout(sheetTimerRef.current);
    void Promise.all(sheetIds.map(id => startCollectionBuildDesignSheet(id)))
      .then(records => {
        const nextSessionId = records.find(item => item.sessionId)?.sessionId;
        if (nextSessionId) {
          setSessionId(nextSessionId);
          watchSession(nextSessionId);
        }
      })
      .catch(error => {
        setSheetsPhase("hidden");
        toast.error(
          getCollectionBuildErrorMessage(
            error,
            "Unable to start design sheets."
          )
        );
      });
  };

  const openSheetFeedback = (value: "up" | "down") => {
    setSheetLiked(value);
    setLiked(value);
    setFeedbackReason("");
    setFeedbackDetails("");
    setFeedbackOpen(true);
  };

  const handleDownloadSheet = async (format: ImageGeneratorDownloadFormat) => {
    if (!selectedLook || sheetDownloading) {
      return;
    }
    const slots = selectedSheetSlots
      .map(slot => {
        const pngPreview = resolveSheetSlotPreviewUrl(slot.id, slot.imageUrl);
        const previewUrl =
          format === "svg" && slot.svgUrl ? slot.svgUrl : pngPreview;
        return {
          id: slot.id,
          label: slot.label || "Sheet",
          previewUrl,
        };
      })
      .filter(slot => Boolean(slot.previewUrl));
    if (!slots.length) {
      return;
    }
    if (
      format === "svg" &&
      selectedSheetSlots.some(slot => slot.imageUrl && !slot.svgUrl)
    ) {
      toast(
        "Some slides have no structured SVG yet — those downloads will be a single PNG wrapper (not separate editable images). Regenerate design sheets after deploy for full SVG embeds."
      );
    }
    setSheetDownloadOpen(false);
    setSheetDownloading(true);
    const stem = `look-${selectedLook.number}-design-sheet`;
    try {
      if (format === "pdf") {
        await downloadImageGeneratorLookPdf(
          slots.map(slot => ({
            previewUrl: slot.previewUrl,
            label: slot.label,
          })),
          stem
        );
      } else if (slots.length === 1) {
        await downloadImageGeneratorLook(slots[0].previewUrl, stem, format);
      } else {
        await downloadImageGeneratorLooks(
          slots.map((slot, index) => ({
            previewUrl: slot.previewUrl,
            fileName: `${stem}-${index + 1}`,
          })),
          format
        );
      }
      toast.success("Design sheet download started.");
    } catch {
      toast.error("Unable to download design sheet.");
    } finally {
      setSheetDownloading(false);
    }
  };

  const handleCopySheet = async () => {
    if (!selectedDesignSheetUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(selectedDesignSheetUrl);
      toast.success("Image link copied.");
    } catch {
      toast.error("Unable to copy.");
    }
  };

  const openSheetRegen = () => {
    if (!selectedLook || sheetRegenBusy) {
      return;
    }
    setSheetRegenOpen(open => !open);
  };

  const handleSheetGenerate = () => {
    if (!selectedLook || sheetRegenBusy) {
      return;
    }
    const outputId = selectedLookViewed?.outputId;
    if (!outputId) {
      toast.error("No look output to regenerate design sheet.");
      return;
    }
    setSheetRegenBusy(true);
    void startCollectionBuildDesignSheet(outputId, { force: true })
      .then(record => {
        if (record.sessionId) {
          watchSession(record.sessionId);
        }
      })
      .catch(error => {
        setSheetRegenBusy(false);
        toast.error(
          getCollectionBuildErrorMessage(
            error,
            "Unable to regenerate design sheet."
          )
        );
      });
  };

  const openApplyMultipleModal = (
    draftIds: string[],
    purpose: MultiSelectPurpose = "regen"
  ) => {
    setMultiSelectPurpose(purpose);
    setMultiDraftIds(draftIds);
    setMultiSelectOpen(true);
  };

  const confirmApplyMultiple = () => {
    if (multiSelectPurpose === "design-sheet") {
      handleConfirmGenerateDesignSheets(multiDraftIds);
      return;
    }
    if (!multiDraftIds.length) {
      return;
    }
    const allIds = looks.map(look => look.id);
    const allSelected =
      allIds.length > 0 && allIds.every(id => multiDraftIds.includes(id));
    setApplyMode(allSelected ? "all" : "multiple");
    setSelectedRegenLookIds(allSelected ? allIds : multiDraftIds);
    setMultiSelectOpen(false);
    setRegenFaces(prev => (prev.length ? prev : ["front", "back"]));
    setRegenOpen(true);
  };

  const openLooksRegen = (lookId?: string, _face?: LookFace) => {
    if (looksPhase !== "ready" || looksRegenBusy) {
      return;
    }
    if (lookId) {
      setSelectedLookId(lookId);
      setApplyMode("multiple");
      setSelectedRegenLookIds([lookId]);
      setRegenFaces(["front", "back"]);
      setMultiSelectOpen(false);
      setRegenOpen(true);
      return;
    }
    if (regenOpen) {
      setRegenOpen(false);
      setMultiSelectOpen(false);
      return;
    }
    setRegenFaces(prev => (prev.length ? prev : ["front", "back"]));
    setRegenOpen(true);
    // First open with no targets yet — prompt look multi-select (screenshot 2).
    if (applyMode === "multiple" && selectedRegenLookIds.length === 0) {
      openApplyMultipleModal([]);
    }
  };

  const handleLooksGenerate = () => {
    if (!canGenerateLooksRegen) {
      return;
    }
    const lookIds = targetedLookIds;
    const faces: LookFace[] =
      regenFaces.length === 1 ? regenFaces : ["front", "back"];
    const keys = lookIds.flatMap(id =>
      faces.map(face => lookFaceKey(id, face))
    );
    const stamp = Date.now();
    setRegenOpen(false);
    setMultiSelectOpen(false);
    setLightbox(null);
    setSelectedLookId(lookIds[0] || selectedLookId);
    setRegenKeys(keys);
    const outputIds = lookIds
      .map(id =>
        toCollectionBuildOutputId(
          looks.find(look => look.id === id)?.versions[0]?.outputId
        )
      )
      .filter((id): id is number => id != null);
    void (async () => {
      for (const outputId of outputIds) {
        try {
          const record = await regenerateCollectionBuild(outputId, {
            additionalPrompt: looksPrompt.trim() || undefined,
            views: faces,
          });
          if (record.sessionId) {
            setSessionId(record.sessionId);
            watchSession(record.sessionId);
          }
        } catch (error) {
          toast.error(
            getCollectionBuildErrorMessage(error, "Unable to regenerate look.")
          );
        }
      }
    })();
    window.clearTimeout(looksTimerRef.current);
    looksTimerRef.current = window.setTimeout(() => {
      setLooks(prev =>
        prev.map(look => {
          if (!lookIds.includes(look.id)) {
            return look;
          }
          const latest = look.versions[0];
          const pair = lookImagePairAt(look.versions.length);
          return {
            ...look,
            versions: [
              {
                id: `${look.id}-${stamp}`,
                frontUrl: faces.includes("front")
                  ? pair.front
                  : latest.frontUrl,
                backUrl: faces.includes("back") ? pair.back : latest.backUrl,
              },
              ...look.versions,
            ],
          };
        })
      );
      setViewedByLookId(prev => {
        const next = { ...prev };
        lookIds.forEach(id => {
          next[id] = `${id}-${stamp}`;
        });
        return next;
      });
      setRegenKeys([]);
      toast.success(
        lookIds.length > 1 ? "Looks regenerated." : "Look regenerated."
      );
    }, MOCK_GENERATE_DELAY_MS);
  };

  const openLightbox = (
    look: CollectionBuildLook,
    face: LookFace,
    url: string
  ) => {
    setSelectedLookId(look.id);
    setLightbox({ lookId: look.id, face, url });
  };

  const renderHistoryThumb = (
    item: CollectionBuildMoodboardVersion,
    options?: { badge?: number; current?: boolean }
  ) => (
    <button
      key={item.id}
      type="button"
      onClick={() => setViewedId(item.id)}
      className={`relative w-full overflow-hidden rounded-lg ${
        options?.current
          ? "border-2 border-[#E8C4B8]"
          : `border ${collectionBuildBorderClassName}`
      }`}
    >
      <div className="aspect-[3/2] bg-lightGray">
        <img
          src={moodboardPreviews[item.id] || item.imageUrl}
          alt="Moodboard version"
          className="h-full w-full object-cover"
        />
      </div>
      {options?.badge != null && (
        <span className="absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-secondary shadow">
          {options.badge}
        </span>
      )}
    </button>
  );

  const renderLookVersionThumb = (
    item: CollectionBuildLookVersion,
    options?: { current?: boolean }
  ) => (
    <button
      key={item.id}
      type="button"
      onClick={() => {
        if (!selectedLook) {
          return;
        }
        setViewedByLookId(prev => ({ ...prev, [selectedLook.id]: item.id }));
      }}
      className={`flex w-full gap-1 overflow-hidden rounded-lg bg-[#F7F3EE] p-0.5 ${
        options?.current
          ? "border-2 border-[#E8C4B8]"
          : `border ${collectionBuildBorderClassName}`
      }`}
    >
      {lookVersionShowGenerating(item) ? (
        <LookPairBusyFaces />
      ) : lookVersionPairReady(item) ? (
        <>
          <div className="aspect-[3/4] w-1/2 overflow-hidden rounded-lg bg-[#F7F3EE]">
            <img
              src={item.frontUrl}
              alt="Look front"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="aspect-[3/4] w-1/2 overflow-hidden rounded-lg bg-[#F7F3EE]">
            <img
              src={item.backUrl}
              alt="Look back"
              className="h-full w-full object-cover"
            />
          </div>
        </>
      ) : (
        <>
          <div className="aspect-[3/4] w-1/2 overflow-hidden rounded-lg bg-[#F7F3EE]" />
          <div className="aspect-[3/4] w-1/2 overflow-hidden rounded-lg bg-[#F7F3EE]" />
        </>
      )}
    </button>
  );

  const renderSheetVersionThumb = (item: CollectionBuildSheetVersion) => {
    const coverSlot = item.slots?.[0];
    const previewUrl = coverSlot
      ? resolveSheetSlotPreviewUrl(coverSlot.id, coverSlot.imageUrl)
      : resolveSheetSlotPreviewUrl(item.id, item.imageUrl);
    return (
      <button
        key={item.id}
        type="button"
        disabled={sheetsBusy}
        onClick={() => {
          if (!selectedLook) {
            return;
          }
          setViewedSheetByLookId(prev => ({
            ...prev,
            [selectedLook.id]: item.id,
          }));
          const firstSlotId = item.slots?.[0]?.id;
          if (firstSlotId) {
            setViewedSheetSlotByLookId(prev => ({
              ...prev,
              [selectedLook.id]: firstSlotId,
            }));
          }
        }}
        className={`relative w-full overflow-hidden rounded-lg ${
          viewedSheetId === item.id
            ? "border-2 border-[#E8C4B8]"
            : `border ${collectionBuildBorderClassName}`
        }`}
      >
        <div className="aspect-[3/2] bg-lightGray">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Design sheet version"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-tertiary">
              Loading…
            </div>
          )}
        </div>
      </button>
    );
  };

  const renderLookFace = (
    look: CollectionBuildLook,
    face: LookFace,
    url: string,
    versionId: string,
    splitAfter?: boolean
  ) => {
    const faceKey = lookFaceKey(look.id, face);
    const regenerating = regenKeys.includes(faceKey);
    const toolsDisabled = looksBusy || looksRegenBusy;
    const imageUrl = resolveLookFacePreviewUrl(versionId, face, url);
    const hasImage = Boolean(imageUrl);
    return (
      <div
        className={`relative min-w-0 flex-1 ${
          splitAfter ? `border-r ${collectionBuildDividerClassName}` : ""
        }`}
      >
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#F7F3EE]">
          {regenerating ? (
            <ImageGeneratorBusyOverlay
              mode="regen"
              outputType="Style"
              variant="inline"
              compact
            />
          ) : hasImage ? (
            <>
              <button
                type="button"
                onClick={() => openLightbox(look, face, imageUrl)}
                className="absolute inset-0 focus:outline-none"
                aria-label={`Enlarge look ${look.number} ${face}`}
              >
                <img
                  src={imageUrl}
                  alt={`Look ${look.number} ${face}`}
                  className="h-full w-full object-cover"
                />
              </button>
              <LookImageTools
                disabled={toolsDisabled}
                downloading={lookDownloadingKey === faceKey}
                downloadOpen={lookDownloadKey === faceKey}
                showRegenerate={showingLooks}
                onRegenerate={() => openLooksRegen(look.id, face)}
                onToggleDownload={() =>
                  setLookDownloadKey(current =>
                    current === faceKey ? null : faceKey
                  )
                }
                onDownloadFormat={format => {
                  void handleLookFaceDownload(look.id, face, imageUrl, format);
                }}
                onCopy={() => copyLookImage(imageUrl)}
                onExpand={() => openLightbox(look, face, imageUrl)}
              />
            </>
          ) : (
            <div
              className="absolute inset-0 bg-[#F7F3EE]"
              aria-label={`Look ${look.number} ${face} unavailable`}
            />
          )}
        </div>
      </div>
    );
  };

  const renderLooksRegenChrome = () => (
    <LooksRegenChrome
      looks={looks}
      applyMode={applyMode}
      selectedLookIds={selectedRegenLookIds}
      faces={regenFaces}
      prompt={looksPrompt}
      promptTitle={looksRegenCopy.title}
      promptRequired={looksRegenCopy.required}
      disabled={looksBusy || looksRegenBusy}
      onApplyAll={() => {
        setApplyMode("all");
        setSelectedRegenLookIds(looks.map(look => look.id));
        setMultiSelectOpen(false);
      }}
      onOpenMultiple={() => {
        setApplyMode("multiple");
        openApplyMultipleModal(
          applyMode === "all"
            ? looks.map(look => look.id)
            : selectedRegenLookIds
        );
      }}
      onToggleFace={face => {
        setRegenFaces(prev =>
          prev.includes(face)
            ? prev.filter(item => item !== face)
            : [...prev, face]
        );
      }}
      onRemoveLookId={id => {
        setApplyMode("multiple");
        setSelectedRegenLookIds(prev => prev.filter(item => item !== id));
      }}
      onPromptChange={setLooksPrompt}
    />
  );

  const selectedLookVersionApproved = Boolean(selectedLookViewed?.approvedAt);
  const canProceedToDesignSheet =
    looksPhase === "ready" &&
    sheetsPhase === "hidden" &&
    !looksBusy &&
    !looksRegenBusy &&
    lookbookApprovedForSheets;
  const pinLeftColumn = detailsOpen;
  /** Active stage keeps Design Details; prior/finalized stages free that column. */
  const moodboardStageActive = showingMoodboard;
  const looksStageActive = showingLooks;
  const stageGridClassName = (
    rail: "none" | "moodboard" | "looks",
    { withDetails = true }: { withDetails?: boolean } = {}
  ) => {
    if (!withDetails) {
      if (rail === "none" || !railOpen) {
        return "xl:grid-cols-[minmax(0,1fr)]";
      }
      if (rail === "looks") {
        return "xl:grid-cols-[minmax(0,1fr)_11.5rem]";
      }
      return "xl:grid-cols-[minmax(0,1fr)_112px]";
    }
    if (rail === "none" || !railOpen) {
      return pinLeftColumn
        ? "xl:grid-cols-[auto_minmax(0,1fr)]"
        : "xl:grid-cols-[40px_minmax(0,1fr)]";
    }
    if (rail === "looks") {
      return pinLeftColumn
        ? "xl:grid-cols-[auto_minmax(0,1fr)_11.5rem]"
        : "xl:grid-cols-[40px_minmax(0,1fr)_11.5rem]";
    }
    return pinLeftColumn
      ? "xl:grid-cols-[auto_minmax(0,1fr)_112px]"
      : "xl:grid-cols-[40px_minmax(0,1fr)_112px]";
  };

  const renderDesignDetailsColumn = () => (
    <div
      className={
        detailsOpen
          ? STAGE_SIDE_MATCH_HEIGHT_CLASS
          : "flex min-w-0 flex-col self-start"
      }
    >
      {detailsOpen ? (
        <CollectionBuildDesignDetails
          details={details}
          onCollapse={() => setDetailsOpen(false)}
        />
      ) : (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            aria-label="Expand design details"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-light text-tertiary hover:bg-lightGray hover:text-secondary focus:outline-none"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M4.5 2.5 8 6 4.5 9.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );

  const renderStageRailToggle = () =>
    railOpen ? (
      <button
        type="button"
        onClick={() => setRailOpen(false)}
        className={renderRailButtonClassName}
        aria-label="Collapse version rail"
      >
        <CollapseIcon fill="#4d4d4d" />
        Render
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setRailOpen(true)}
        className={renderRailButtonClassName}
        aria-label="Expand version rail"
      >
        <CollapseIcon fill="#4d4d4d" />
      </button>
    );

  return (
    <div ref={rootRef} className="mt-6 flex min-w-0 max-w-full flex-col gap-2">
      {showingError && generationFailure ? (
        <div
          className={`grid grid-cols-1 content-start items-start gap-x-6 gap-y-4 min-h-[calc(100vh-180px)] ${stageGridClassName("none")}`}
        >
          {renderDesignDetailsColumn()}
          <section className="flex min-w-0 flex-col">
            <CollectionBuildGenerationError
              title={generationFailure.title}
              reason={generationFailure.reason}
              retrying={retrying}
              onRetry={() => {
                void handleRetryGeneration();
              }}
              onBackToQuery={onBackToQuery}
            />
          </section>
        </div>
      ) : null}

      {showMoodboardSection ? (
        <section
          ref={moodboardStageRef}
          className={`${STAGE_SECTION_CLASS}${
            moodboardStageActive && generating
              ? ` ${STAGE_SECTION_ACTIVE_MIN_H_CLASS}`
              : ""
          }`}
        >
          <div className="mb-2 flex min-w-0 items-center justify-between gap-3">
            <h2 className="flex min-w-0 flex-wrap items-center gap-2 text-lg font-semibold leading-6 text-secondary">
              Moodboard
              {showingMoodboard &&
              (viewedMoodboardApproved ||
                collectionMoodboardApproved(session)) ? (
                <span className="flex items-center gap-1.5 text-sm font-medium text-positive">
                  <span className="h-2 w-2 rounded-full bg-positive" />
                  Approved
                </span>
              ) : null}
            </h2>
            <div className="flex shrink-0 items-center gap-2">
              {renderStageRailToggle()}
            </div>
          </div>
          <div
            className={`grid grid-cols-1 items-stretch gap-x-6 gap-y-4 xl:gap-x-5 ${stageGridClassName("moodboard", { withDetails: moodboardStageActive })}`}
          >
            {moodboardStageActive ? renderDesignDetailsColumn() : null}
            <section
              className={`flex min-w-0 flex-col self-start ${
                showingMoodboard && generating
                  ? "min-h-[40rem] self-stretch"
                  : ""
              }`}
            >
              {showingMoodboard && generating ? (
                <InlineBusyCanvas mode={busyMode} kind="moodboard" />
              ) : (
                <div className="flex w-full flex-col items-start gap-1.5">
                  <div
                    className={`relative flex w-full items-center justify-center overflow-hidden rounded-lg border ${collectionBuildBorderClassName} bg-white`}
                  >
                    {moodboardImageUrl || viewed ? (
                      <img
                        src={moodboardImageUrl || viewedPreviewUrl}
                        alt="Generated moodboard"
                        className="mx-auto block h-auto w-full max-w-full object-contain"
                      />
                    ) : null}
                  </div>
                  {viewed ? (
                    <div className="flex items-center gap-2.5">
                      <div className="relative" ref={moodboardDownloadRef}>
                        <button
                          type="button"
                          onClick={() =>
                            setMoodboardDownloadOpen(open => !open)
                          }
                          disabled={moodboardDownloading}
                          className={moodboardToolBtnClassName}
                          aria-label="Download"
                          aria-haspopup="menu"
                          aria-expanded={moodboardDownloadOpen}
                        >
                          <DownloadIcon className={iconGlyphClassName} />
                        </button>
                        {moodboardDownloadOpen ? (
                          <div
                            role="menu"
                            className={`${generativeDownloadMenuClassName} left-0 top-full mt-2`}
                          >
                            {MOODBOARD_DOWNLOAD_FORMATS.map(option => (
                              <button
                                key={option.value}
                                type="button"
                                role="menuitem"
                                onClick={() =>
                                  void handleMoodboardDownload(option.value)
                                }
                                className={generativeDownloadMenuItemClassName}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => openFeedback("up")}
                        className={`${moodboardToolBtnClassName} ${
                          liked === "up" ? "text-primary" : ""
                        }`}
                        aria-label="Like"
                      >
                        <ThumbIcon
                          filled={liked === "up"}
                          className="rotate-180"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => openFeedback("down")}
                        className={`${moodboardToolBtnClassName} ${
                          liked === "down" ? "text-primary" : ""
                        }`}
                        aria-label="Dislike"
                      >
                        <ThumbIcon filled={liked === "down"} />
                      </button>
                      <button
                        type="button"
                        onClick={handleCopy}
                        className={moodboardToolBtnClassName}
                        aria-label="Copy"
                      >
                        <CopyIcon className={iconGlyphClassName} />
                      </button>
                      {moodboardStageActive ? (
                        <button
                          type="button"
                          onClick={toggleRegenInput}
                          className={moodboardToolBtnClassName}
                          aria-label="Regenerate"
                          aria-expanded={showInput}
                        >
                          <RegenerateIcon className="h-6 w-6 text-primary" />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </section>
            {railOpen ? (
              <aside className={STAGE_SIDE_MATCH_HEIGHT_CLASS}>
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  <div className="shrink-0">
                    <p className="mb-2 text-sm font-medium text-secondary">
                      Current
                    </p>
                    {generating ? (
                      <div className="relative w-full overflow-hidden rounded-2xl border-2 border-[#E8C4B8]">
                        <div className="relative aspect-[3/2] bg-[#F7F3EE]">
                          <ImageGeneratorBusyOverlay
                            mode={busyMode}
                            outputType="Style"
                            variant="inline"
                            compact
                          />
                        </div>
                      </div>
                    ) : current ? (
                      renderHistoryThumb(current, { current: true })
                    ) : (
                      <p className="text-xs text-tertiary">None</p>
                    )}
                  </div>
                  {previous.length > 0 && (
                    <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
                      <p className="mb-2 shrink-0 text-sm font-medium text-secondary">
                        Previous
                      </p>
                      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
                        {previous.map((item, index) =>
                          renderHistoryThumb(item, {
                            badge: previous.length - index,
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            ) : null}
          </div>
          {showingMoodboard ? (
            <div className={showInput ? "mt-4" : "mt-8"}>
              {!generating && !showInput && moodboardAfterCopy ? (
                <p className="mb-2 text-sm text-secondary">
                  {moodboardAfterCopy}
                </p>
              ) : null}
              {showInput && (
                <>
                  <p className="mb-2 text-sm text-secondary">
                    {moodboardRegenCopy.title ||
                      (moodboardRegenCopy.required
                        ? "Enter Input"
                        : "Enter Input (optional)")}
                  </p>
                  <textarea
                    value={prompt}
                    onChange={event => setPrompt(event.target.value)}
                    placeholder="Add more blue/ blue green images. The board leans more towards pink and peach themes"
                    rows={moodboardRegenCopy.required ? 4 : 3}
                    required={moodboardRegenCopy.required}
                    aria-required={moodboardRegenCopy.required || undefined}
                    className={
                      moodboardRegenCopy.required
                        ? promptInputClassName
                        : promptInputCompactClassName
                    }
                  />
                </>
              )}
              <div
                className={`flex flex-wrap justify-end gap-3 ${
                  showInput
                    ? "mt-2"
                    : !generating && moodboardAfterCopy
                      ? "mt-4"
                      : ""
                }`}
              >
                {showInput && (
                  <button
                    type="button"
                    disabled={
                      generating ||
                      (moodboardRegenCopy.required && !prompt.trim())
                    }
                    onClick={handleGenerate}
                    className={`${mutedButtonClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    {generating
                      ? collectionBuildBusyTitle("moodboard", busyMode)
                      : "Generate"}
                  </button>
                )}
                {!viewedMoodboardApproved ? (
                  <button
                    type="button"
                    disabled={generating || !viewed}
                    onClick={handleApprove}
                    className={`${submitButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    Approve
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={!viewedMoodboardApproved || looksBusy}
                  onClick={handleProceedToLooks}
                  className={`${outlineActionClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  Proceed to Looks
                </button>
                <button
                  type="button"
                  disabled={
                    moodboardLibrarySaved ||
                    viewed?.outputId == null ||
                    generating ||
                    !viewed
                  }
                  onClick={() => openSaveModal(viewed?.outputId)}
                  className={`${outlineButtonClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {moodboardLibrarySaved ? "Saved" : "Save to Library"}
                </button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {showLooksSection ? (
        <section
          ref={looksStageRef}
          className={`${STAGE_SECTION_CLASS}${
            looksStageActive && (looksBusy || looksRegenBusy)
              ? ` ${STAGE_SECTION_ACTIVE_MIN_H_CLASS}`
              : ""
          }`}
        >
          <div className="mb-2 flex min-w-0 items-center justify-between gap-3">
            <h2 className="flex min-w-0 flex-wrap items-center gap-2 text-lg font-semibold leading-6 text-secondary">
              Look Book
              {showingLooks &&
              (looksApproved || selectedLookVersionApproved) ? (
                <span className="flex items-center gap-1.5 text-sm font-medium text-positive">
                  <span className="h-2 w-2 rounded-full bg-positive" />
                  Approved
                </span>
              ) : null}
            </h2>
            <div className="flex shrink-0 items-center gap-3">
              {renderStageRailToggle()}
              <div className="relative" ref={looksHeaderDownloadRef}>
                <button
                  type="button"
                  disabled={
                    looksBusy ||
                    looksHeaderDownloading ||
                    (!looksDownloadRef.current.frontUrl &&
                      !looksDownloadRef.current.backUrl)
                  }
                  onClick={() => setLooksHeaderDownloadOpen(open => !open)}
                  aria-label="Download looks"
                  aria-haspopup="menu"
                  aria-expanded={looksHeaderDownloadOpen}
                  className={stageHeaderDownloadBtnClassName}
                >
                  <DownloadIcon className="h-5 w-5 [&_path]:fill-current" />
                </button>
                {looksHeaderDownloadOpen ? (
                  <div
                    role="menu"
                    className={`${generativeDownloadMenuClassName} right-0 top-full mt-2`}
                  >
                    {LOOK_DOWNLOAD_FORMATS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        role="menuitem"
                        onClick={() =>
                          void handleLooksHeaderDownload(option.value)
                        }
                        className={generativeDownloadMenuItemClassName}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div
            className={`grid grid-cols-1 items-stretch gap-x-6 gap-y-4 xl:gap-x-5 ${stageGridClassName("looks", { withDetails: looksStageActive })}`}
          >
            {looksStageActive ? renderDesignDetailsColumn() : null}
            <section className="flex min-w-0 flex-col self-start">
              {looksRegenBusy || (looksBusy && looks.length === 0) ? (
                <LooksBusyGrid
                  mode={looksBusy ? "generate" : "regen"}
                  slotCount={lookBusySlotCount}
                />
              ) : (
                <>
                  <div
                    className={`grid w-full min-w-0 grid-cols-1 gap-4 sm:gap-5 ${
                      lookPageSlotCount > 1 ? "md:grid-cols-2" : ""
                    }`}
                  >
                    {pagedLooks.map(look => {
                      const version: CollectionBuildLookVersion | undefined =
                        viewedVersionFor(look);
                      const pairReady = lookVersionPairReady(version);
                      const showGenerating =
                        !version || lookVersionShowGenerating(version);
                      return (
                        <article
                          key={look.id}
                          onClick={() => setSelectedLookId(look.id)}
                          className={`relative w-full min-w-0 overflow-hidden rounded-lg border ${collectionBuildBorderClassName} bg-[#F7F3EE] ${
                            lookPageSlotCount === 1
                              ? "max-w-[min(100%,36rem)]"
                              : ""
                          }`}
                        >
                          <span className={lookNumberBadgeClassName}>
                            {look.number}
                          </span>
                          {showGenerating ? (
                            <LookPairBusyFaces
                              mode={looksBusy ? "generate" : "regen"}
                            />
                          ) : pairReady && version ? (
                            <div className="flex w-full">
                              {renderLookFace(
                                look,
                                "front",
                                version.frontUrl,
                                version.id,
                                true
                              )}
                              {renderLookFace(
                                look,
                                "back",
                                version.backUrl,
                                version.id
                              )}
                            </div>
                          ) : version ? (
                            <div className="flex w-full">
                              {renderLookFace(
                                look,
                                "front",
                                "",
                                version.id,
                                true
                              )}
                              {renderLookFace(look, "back", "", version.id)}
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                  <LookBookPager
                    page={lookBookPage}
                    pageCount={lookBookPageCount}
                    onPageChange={handleLookBookPageChange}
                  />
                </>
              )}
            </section>
            {railOpen ? (
              <aside className={STAGE_SIDE_MATCH_HEIGHT_CLASS}>
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  <div className="shrink-0">
                    <p className="mb-2 text-sm font-medium text-secondary">
                      Current Render
                    </p>
                    {looksRegenBusy ||
                    (looksBusy && looks.length === 0) ||
                    (selectedLookCurrent &&
                      lookVersionShowGenerating(selectedLookCurrent)) ? (
                      <div className="relative w-full overflow-hidden rounded-xl border-2 border-[#E8C4B8]">
                        <div className="relative aspect-[3/2] bg-[#F7F3EE]">
                          <ImageGeneratorBusyOverlay
                            mode={looksRegenBusy ? "regen" : "generate"}
                            outputType="Style"
                            variant="inline"
                            compact
                          />
                        </div>
                      </div>
                    ) : selectedLookCurrent &&
                      lookVersionPairReady(selectedLookCurrent) ? (
                      renderLookVersionThumb(selectedLookCurrent, {
                        current: true,
                      })
                    ) : (
                      <p className="text-xs text-tertiary">None</p>
                    )}
                  </div>
                  <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
                    <p className="mb-2 shrink-0 text-sm font-medium text-secondary">
                      Previous Render
                    </p>
                    {selectedLookPrevious.length > 0 ? (
                      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
                        {selectedLookPrevious.map(item =>
                          renderLookVersionThumb(item)
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-tertiary">None</p>
                    )}
                  </div>
                </div>
              </aside>
            ) : null}
          </div>
          {showingLooks ? (
            <>
              {regenOpen && !looksBusy && (
                <div className="mt-8">
                  {renderLooksRegenChrome()}
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      disabled={!canGenerateLooksRegen}
                      onClick={handleLooksGenerate}
                      className={`${mutedButtonClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                      {looksRegenBusy
                        ? collectionBuildBusyTitle("look", "regen")
                        : "Generate"}
                    </button>
                  </div>
                </div>
              )}
              <div
                className={`flex flex-wrap gap-3 ${
                  looksApproved ? "mt-4 justify-end" : "mt-10 justify-end"
                }`}
              >
                {!selectedLookVersionApproved ? (
                  <button
                    type="button"
                    disabled={
                      looksBusy ||
                      looksRegenBusy ||
                      !looks.length ||
                      !lookVersionPairReady(selectedLookViewed)
                    }
                    onClick={handleApproveLooks}
                    className={`${submitButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    Approve
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={
                    looksLibrarySaved ||
                    selectedLookViewed?.outputId == null ||
                    looksBusy ||
                    looksRegenBusy ||
                    !lookVersionPairReady(selectedLookViewed)
                  }
                  onClick={() => openSaveModal(selectedLookViewed?.outputId)}
                  className={`${outlineButtonClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {looksLibrarySaved ? "Saved" : "Save to Library"}
                </button>
                <button
                  type="button"
                  disabled={!canProceedToDesignSheet}
                  onClick={handleProceedToDesignSheet}
                  title={
                    lookbookApprovedForSheets
                      ? "Choose which looks get design sheets"
                      : "Approve the lookbook first"
                  }
                  className={`${outlineActionClassName} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  Proceed to Design Sheet
                </button>
              </div>
              {!canProceedToDesignSheet && looksPhase === "ready" ? (
                <p className="mt-2 text-right text-xs text-secondary/80">
                  Approve the lookbook to enable Proceed to Design Sheet.
                </p>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}

      {showDesignSheetSection ? (
        <section
          ref={designSheetStageRef}
          className={`${STAGE_SECTION_CLASS}${
            sheetsBusy ? ` ${STAGE_SECTION_ACTIVE_MIN_H_CLASS}` : ""
          }`}
        >
          <div className="mb-2 flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="flex min-w-0 flex-wrap items-center gap-2 text-lg font-semibold leading-6 text-secondary">
                Design Sheet
              </h2>
              <p className={`${helperTextClassName} mt-1`}>
                This design was generated by AI for reference and ideation only.
                Please have it reviewed before it&apos;s used for costing,
                sourcing, or production.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {renderStageRailToggle()}
              <div className="relative" ref={sheetHeaderDownloadRef}>
                <button
                  type="button"
                  disabled={
                    !selectedDesignSheetUrl || sheetsBusy || sheetDownloading
                  }
                  onClick={() => setSheetDownloadOpen(open => !open)}
                  aria-label="Download design sheet"
                  aria-haspopup="menu"
                  aria-expanded={sheetDownloadOpen}
                  className={stageHeaderDownloadBtnClassName}
                >
                  <DownloadIcon className="h-5 w-5 [&_path]:fill-current" />
                </button>
                {sheetDownloadOpen ? (
                  <div
                    role="menu"
                    className={`${generativeDownloadMenuClassName} right-0 top-full mt-2`}
                  >
                    {DESIGN_SHEET_DOWNLOAD_FORMATS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        role="menuitem"
                        onClick={() => void handleDownloadSheet(option.value)}
                        className={generativeDownloadMenuItemClassName}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div
            className={`grid grid-cols-1 items-stretch gap-x-6 gap-y-4 xl:gap-x-5 ${stageGridClassName("looks")}`}
          >
            {renderDesignDetailsColumn()}
            <section
              className={`flex min-w-0 flex-col self-start ${
                sheetsBusy ? "min-h-[40rem] self-stretch" : ""
              }`}
            >
              {sheetsGenerating ? (
                <InlineBusyCanvas mode="generate" kind="design-sheet" />
              ) : (
                <>
                  <div className="mb-4 flex min-w-0 max-w-full flex-wrap items-center gap-[13px]">
                    {designSheetLooks.map(look => (
                      <button
                        key={look.id}
                        type="button"
                        onClick={() => setSelectedLookId(look.id)}
                        className={lookTabClassName(look.id === selectedLookId)}
                      >
                        Look {look.number}
                      </button>
                    ))}
                  </div>
                  {selectedSheetSlots.length > 1 ? (
                    <div className="mb-3 flex min-w-0 max-w-full flex-wrap items-center gap-2">
                      {selectedSheetSlots.map(slot => (
                        <button
                          key={slot.id}
                          type="button"
                          onClick={() => {
                            if (!selectedLook) {
                              return;
                            }
                            setViewedSheetSlotByLookId(prev => ({
                              ...prev,
                              [selectedLook.id]: slot.id,
                            }));
                          }}
                          className={lookTabClassName(
                            viewedSheetSlotId === slot.id
                          )}
                        >
                          {slot.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {sheetRegenBusy ? (
                    <InlineBusyCanvas mode="regen" kind="design-sheet" />
                  ) : (
                    <div className="flex w-full flex-col items-start gap-2.5">
                      <div
                        className={`relative min-w-0 max-w-full overflow-hidden rounded-lg border ${collectionBuildBorderClassName} bg-white`}
                      >
                        {selectedDesignSheetUrl ? (
                          <img
                            src={selectedDesignSheetUrl}
                            alt={`Look ${selectedLook?.number || ""} design sheet`}
                            className="block h-auto w-full max-w-full object-contain"
                          />
                        ) : null}
                      </div>
                      {selectedDesignSheetUrl ? (
                        <div className="flex items-center gap-2.5">
                          <div
                            className="relative"
                            ref={sheetToolbarDownloadRef}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setSheetDownloadOpen(open => !open)
                              }
                              disabled={sheetDownloading}
                              className={sheetToolBtnClassName}
                              aria-label="Download"
                              aria-haspopup="menu"
                              aria-expanded={sheetDownloadOpen}
                            >
                              <DownloadIcon className={iconGlyphClassName} />
                            </button>
                            {sheetDownloadOpen ? (
                              <div
                                role="menu"
                                className={`${generativeDownloadMenuClassName} left-0 top-full mt-2`}
                              >
                                {DESIGN_SHEET_DOWNLOAD_FORMATS.map(option => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    role="menuitem"
                                    onClick={() =>
                                      void handleDownloadSheet(option.value)
                                    }
                                    className={
                                      generativeDownloadMenuItemClassName
                                    }
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={openSheetRegen}
                            className={sheetToolBtnClassName}
                            aria-label="Regenerate"
                            aria-expanded={sheetRegenOpen}
                          >
                            <RegenerateIcon className="h-6 w-6 text-primary" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openSheetFeedback("up")}
                            className={`${sheetToolBtnClassName} ${
                              sheetLiked === "up" ? "text-primary" : ""
                            }`}
                            aria-label="Like"
                          >
                            <ThumbIcon
                              filled={sheetLiked === "up"}
                              className="rotate-180"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => openSheetFeedback("down")}
                            className={`${sheetToolBtnClassName} ${
                              sheetLiked === "down" ? "text-primary" : ""
                            }`}
                            aria-label="Dislike"
                          >
                            <ThumbIcon filled={sheetLiked === "down"} />
                          </button>
                          <button
                            type="button"
                            onClick={handleCopySheet}
                            className={sheetToolBtnClassName}
                            aria-label="Copy"
                          >
                            <CopyIcon className={iconGlyphClassName} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                  {sheetRegenOpen && !sheetRegenBusy && (
                    <div className="mt-6">
                      <p className="mb-2 text-sm text-secondary">
                        Input <span className="text-tertiary">(optional)</span>
                      </p>
                      <textarea
                        value={sheetPrompt}
                        onChange={event => setSheetPrompt(event.target.value)}
                        placeholder="Describe how this design sheet should change"
                        className={promptInputClassName}
                      />
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={handleSheetGenerate}
                          className={mutedButtonClassName}
                        >
                          Generate
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
            {railOpen ? (
              <aside className={STAGE_SIDE_MATCH_HEIGHT_CLASS}>
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  <div className="shrink-0">
                    <p className="mb-2 text-sm font-medium text-secondary">
                      Current Render
                    </p>
                    {sheetsBusy ? (
                      <div className="relative w-full overflow-hidden rounded-2xl border-2 border-[#E8C4B8]">
                        <div className="relative aspect-[3/2] bg-[#F7F3EE]">
                          <ImageGeneratorBusyOverlay
                            mode={sheetRegenBusy ? "regen" : "generate"}
                            outputType="Style"
                            variant="inline"
                            compact
                          />
                        </div>
                      </div>
                    ) : selectedSheetCurrent ? (
                      renderSheetVersionThumb(selectedSheetCurrent)
                    ) : (
                      <p className="text-xs text-tertiary">None</p>
                    )}
                  </div>
                  <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
                    <p className="mb-2 shrink-0 text-sm font-medium text-secondary">
                      Previous
                    </p>
                    {selectedSheetPrevious.length > 0 ? (
                      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
                        {selectedSheetPrevious.map(item =>
                          renderSheetVersionThumb(item)
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-tertiary">None</p>
                    )}
                  </div>
                </div>
              </aside>
            ) : null}
          </div>
        </section>
      ) : null}

      {multiSelectOpen &&
        createPortal(
          <div className="fixed inset-0 z-[60] flex items-center justify-center font-avenir">
            <div
              className="fixed inset-0 bg-black/20"
              onClick={() => setMultiSelectOpen(false)}
            />
            <div
              className={`relative z-10 flex max-h-[min(36rem,calc(100vh-2rem))] w-[min(34rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-[22px] border ${collectionBuildBorderClassName} bg-white shadow-[0_8px_28px_rgba(0,0,0,0.12)]`}
            >
              <div
                className={`flex items-center justify-between gap-3 border-b ${collectionBuildDividerClassName} px-6 py-4`}
              >
                <h2 className="text-[15px] font-medium text-secondary">
                  {multiSelectPurpose === "design-sheet"
                    ? "Proceed to Design Sheet"
                    : "Apply to multiple looks"}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    const allIds = looks.map(look => look.id);
                    const allSelected =
                      allIds.length > 0 &&
                      allIds.every(id => multiDraftIds.includes(id));
                    setMultiDraftIds(allSelected ? [] : allIds);
                  }}
                  className={`h-8 shrink-0 rounded-full border ${collectionBuildBorderClassName} bg-white px-4 font-avenir text-sm text-secondary hover:bg-lightGray focus:outline-none`}
                >
                  Select All
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                  {looks.map(look => (
                    <LookSelectCheckbox
                      key={look.id}
                      checked={multiDraftIds.includes(look.id)}
                      label={`Look ${look.number}`}
                      onToggle={() => {
                        setMultiDraftIds(prev =>
                          prev.includes(look.id)
                            ? prev.filter(id => id !== look.id)
                            : [...prev, look.id]
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
              <div
                className={`flex items-center justify-between gap-3 border-t ${collectionBuildDividerClassName} px-6 py-4`}
              >
                <p className="min-h-[1rem] text-xs text-tertiary">
                  {formatSelectedLooks(looks, multiDraftIds)}
                </p>
                <button
                  type="button"
                  disabled={!multiDraftIds.length}
                  onClick={confirmApplyMultiple}
                  className={
                    multiSelectPurpose === "design-sheet"
                      ? `${outlineButtonClassName} shrink-0 font-medium disabled:cursor-not-allowed disabled:opacity-40`
                      : "h-[46px] shrink-0 rounded-full bg-black px-8 font-avenir text-sm text-white hover:bg-[#111] focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                  }
                >
                  {multiSelectPurpose === "design-sheet"
                    ? "Proceed to Design Sheet"
                    : "Selected"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {feedbackOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center font-avenir">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setFeedbackOpen(false)}
            />
            <div
              className={`relative z-10 max-h-[min(40rem,calc(100vh-2rem))] w-full max-w-2xl overflow-y-auto rounded-3xl border ${collectionBuildBorderClassName} bg-white p-6 shadow-lg`}
            >
              <div
                className={`flex items-center justify-between border-b ${collectionBuildDividerClassName} pb-4`}
              >
                <h2 className="text-xl font-semibold text-primary">
                  Share feedback
                </h2>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(false)}
                  aria-label="Close feedback"
                  className="flex h-8 w-8 items-center justify-center text-2xl leading-none text-tertiary hover:text-primary focus:outline-none"
                >
                  ×
                </button>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {(liked === "up"
                  ? POSITIVE_FEEDBACK_REASONS
                  : NEGATIVE_FEEDBACK_REASONS
                ).map(reason => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() =>
                      setFeedbackReason(currentReason =>
                        currentReason === reason ? "" : reason
                      )
                    }
                    className={exclusivePillClassName(
                      feedbackReason === reason
                    )}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <textarea
                value={feedbackDetails}
                onChange={event => setFeedbackDetails(event.target.value)}
                placeholder="Share details (optional)"
                className={`${textareaClassName} mt-5 min-h-[120px]`}
              />
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleFeedbackSubmit}
                  className={submitButtonClassName}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {saveOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center font-avenir">
            <div
              className="fixed inset-0 bg-black/30"
              onClick={closeSaveModal}
            />
            <div
              className={`relative z-10 max-h-[min(24rem,calc(100vh-2rem))] w-[22rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl border ${collectionBuildBorderClassName} bg-white p-5 shadow-lg`}
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <h2 className="text-base font-semibold text-primary">
                  File Name
                </h2>
                <button
                  type="button"
                  onClick={closeSaveModal}
                  aria-label="Close save dialog"
                  className="flex h-8 w-8 shrink-0 items-center justify-center text-2xl leading-none text-tertiary hover:text-primary focus:outline-none"
                >
                  ×
                </button>
              </div>
              <input
                value={saveName}
                onChange={event => {
                  setSaveName(event.target.value);
                  if (saveNameError) {
                    setSaveNameError("");
                  }
                }}
                placeholder="Name"
                className={saveInputClassName}
                aria-invalid={Boolean(saveNameError)}
                aria-describedby={
                  saveNameError ? "collection-build-save-name-error" : undefined
                }
              />
              {saveNameError ? (
                <p
                  id="collection-build-save-name-error"
                  className="mt-2 text-xs text-red-600"
                >
                  {saveNameError}
                </p>
              ) : null}
              <div className="mt-4 flex items-center justify-between gap-3">
                <p className="text-xs text-tertiary">*Save to Library</p>
                <button
                  type="button"
                  onClick={handleSaveToLibrary}
                  className={submitButtonClassName}
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {lightbox &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center font-avenir">
            <div
              className="fixed inset-0 bg-black/70"
              onClick={() => setLightbox(null)}
            />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              aria-label="Close image"
              className="absolute right-6 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl leading-none text-secondary hover:bg-lightGray focus:outline-none"
            >
              ×
            </button>
            <div className="relative z-10 max-h-[90vh] max-w-[90vw]">
              <img
                src={lightbox.url}
                alt="Look preview"
                className="max-h-[90vh] max-w-[90vw] rounded-2xl bg-[#F7F3EE] object-contain shadow-lg"
              />
              <LookImageTools
                disabled={looksBusy || looksRegenBusy}
                downloading={
                  lookDownloadingKey ===
                  lookFaceKey(lightbox.lookId, lightbox.face)
                }
                downloadOpen={
                  lookDownloadKey ===
                  lookFaceKey(lightbox.lookId, lightbox.face)
                }
                showRegenerate={showingLooks}
                showExpand={false}
                onRegenerate={() => {
                  const target = lightbox;
                  setLightbox(null);
                  openLooksRegen(target.lookId, target.face);
                }}
                onToggleDownload={() => {
                  const key = lookFaceKey(lightbox.lookId, lightbox.face);
                  setLookDownloadKey(current => (current === key ? null : key));
                }}
                onDownloadFormat={format => {
                  void handleLookFaceDownload(
                    lightbox.lookId,
                    lightbox.face,
                    lightbox.url,
                    format
                  );
                }}
                onCopy={() => copyLookImage(lightbox.url)}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export { CollectionBuildResultsBoundary };
export default CollectionBuildResults;
